/**
 * HelpPage — Searchable in-app user manual
 *
 * All manual content is structured as typed data (ARTICLES) so it is
 * fully searchable without an external markdown parser.
 *
 * Structure:
 *   ARTICLES  — flat array of help articles, each with a section, title,
 *               keywords, and an array of content blocks.
 *   Content blocks — { type: 'p' | 'h3' | 'ul' | 'table', ... }
 *
 * The search matches query against: section, title, keywords, and the
 * text of every paragraph / list item / table cell.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCanonical } from '../hooks/useCanonical';
import {
  Search, BookOpen, ChevronRight, X, ArrowLeft,
  LayoutDashboard, Leaf, MapPin, Satellite, Settings, HelpCircle, Flame, MessageCircle, Target, Building, Zap,
} from 'lucide-react';
import Card from '../components/common/Card';
import HelpChat from '../components/common/HelpChat';
import { cn } from '../lib/utils';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type BlockType = 'p' | 'h3' | 'ul' | 'table' | 'note';

interface Block {
  type: BlockType;
  text?: string;               // p / h3 / note
  items?: string[];            // ul
  headers?: string[];          // table
  rows?: string[][];           // table
}

interface Article {
  id: string;
  section: string;
  title: string;
  icon: string;
  keywords: string[];
  blocks: Block[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Content
// ─────────────────────────────────────────────────────────────────────────────

export const ARTICLES: Article[] = [
  // ── Authentication ──────────────────────────────────────────────────────────
  {
    id: 'auth-login',
    section: 'Authentication',
    title: 'Logging in',
    icon: 'HelpCircle',
    keywords: ['login', 'sign in', 'password', 'mfa', 'forgot'],
    blocks: [
      { type: 'p', text: 'Open the application and enter your email and password.' },
      { type: 'p', text: 'If your organisation has MFA enabled, you will be prompted for a one-time code after your password. Codes can come from an authenticator app (TOTP), SMS, or email depending on your MFA method.' },
      { type: 'h3', text: 'Forgot your password?' },
      { type: 'p', text: 'Click "Forgot password?" on the login screen. Enter your email and follow the reset link sent to your inbox.' },
    ],
  },
  {
    id: 'auth-register',
    section: 'Authentication',
    title: 'Creating an account',
    icon: 'HelpCircle',
    keywords: ['register', 'sign up', 'create account', 'organisation'],
    blocks: [
      { type: 'p', text: 'Click "Request access" on the login page. Fill in your full name, email, select your organisation from the list, and choose a password (minimum 6 characters). Accept the Terms of Service, then click "Request access".' },
      { type: 'p', text: 'On success you are automatically signed in and taken to the Dashboard. Your organisation is created automatically — invite team members from Settings → User Management.' },
    ],
  },
  {
    id: 'auth-mfa',
    section: 'Authentication',
    title: 'Setting up MFA',
    icon: 'HelpCircle',
    keywords: ['mfa', 'two factor', '2fa', 'totp', 'authenticator', 'security'],
    blocks: [
      { type: 'p', text: 'Go to Settings → MFA Management to enrol a second factor. Three methods are supported:' },
      { type: 'ul', items: ['TOTP — scan the QR code with any authenticator app (Google Authenticator, Authy, etc.)', 'SMS — receive a code via text message to your registered mobile number', 'Email — receive a code to your account email'] },
      { type: 'p', text: 'Once enrolled, every sign-in requires the second factor. You can disable or replace your MFA method from the same settings page.' },
    ],
  },

  // ── Dashboard ───────────────────────────────────────────────────────────────
  {
    id: 'dashboard-overview',
    section: 'Command Center',
    title: 'Dashboard overview',
    icon: 'LayoutDashboard',
    keywords: ['dashboard', 'home', 'overview', 'kpi', 'stats', 'priority', 'anomaly', 'attention', 'vertical activity', 'quick actions', 'notifications'],
    blocks: [
      { type: 'p', text: 'The Dashboard is the first screen after login. It gives a real-time cross-vertical summary of your operations, organised into seven zones.' },
      { type: 'h3', text: 'Zone 1 — Priority Attention' },
      { type: 'p', text: 'Four tiles at the top highlight items requiring immediate human action. Each tile shows a live count badge and links directly to the relevant action page:' },
      { type: 'ul', items: [
        'Anomalies Pending Review — satellite-detected issues at Step 4 awaiting your decision',
        'Analyses Awaiting Sign-off — AI diagnoses at Step 7 waiting for HITL review or Safety Officer approval',
        'Data Integrity Alerts — cryptographic tamper-detection violations from the audit system',
        'Unread Notifications — unread system alerts and escalations',
      ]},
      { type: 'note', text: 'Anomaly counts are automatically scoped to your active vertical modules. If no vertical modules are enabled, the anomaly tiles show 0 rather than querying the backend.' },
      { type: 'h3', text: 'Zone 2 — Operational Pulse' },
      { type: 'p', text: 'A strip of six stat chips shows cross-vertical live counters: Total Missions, In Progress, Planned, Files Ingested, AI Analyses run, and Monitored Zones. Below these chips, an anomaly severity breakdown (Critical / High / Medium / Low) links through to the relevant anomaly page.' },
      { type: 'h3', text: 'Zone 3 — Recent Missions' },
      { type: 'p', text: 'The last five missions across all verticals with status badges (planned, in_progress, completed, failed). Click any row to open the mission detail page.' },
      { type: 'h3', text: 'Zone 4 — Pending Anomalies' },
      { type: 'p', text: 'The top pending anomalies across active verticals. Each row shows severity, anomaly type, field/corridor name, area (ha), and detection time. Only visible when there are pending anomalies. Click a row to go directly to that vertical\'s anomaly review page.' },
      { type: 'h3', text: 'Zone 5 — Vertical Activity' },
      { type: 'p', text: 'One row per active vertical module with inline KPI chips (anomalies pending, reviews pending, missions in-flight). Only rows for enabled verticals are shown. If no vertical modules are active, a prompt to activate them appears instead.' },
      { type: 'h3', text: 'Zone 6 — Unread Alerts' },
      { type: 'p', text: 'A list of unread system notifications. Only shown when there are unread alerts. Click "View all" to go to the Notifications centre.' },
      { type: 'h3', text: 'Zone 7 — Quick Actions' },
      { type: 'p', text: 'Six contextual shortcut tiles at the bottom of the page:' },
      { type: 'table',
        headers: ['Tile', 'Where it links', 'Note'],
        rows: [
          ['Plan Survey',       'Vertical survey planner', 'Routes to the highest-priority active vertical\'s survey planner, or /missions/create if none'],
          ['Review Anomalies',  'Vertical anomaly page',   'Routes to the highest-priority active vertical\'s anomaly page'],
          ['HITL Sign-off',     'Vertical analysis review','Routes to the highest-priority active vertical\'s analysis review page'],
          ['Mission Control',   '/missions',               'All missions across verticals'],
          ['AI Analysis',       '/analysis',               'Ask Nabhos NLQ workspace'],
          ['Reports / Crop Reports', 'AgroTech reports or anomaly page', 'Label changes to "Crop Reports" when AgroTech is active'],
        ],
      },
      { type: 'h3', text: 'Platform health strip' },
      { type: 'p', text: 'Three summary tiles at the very bottom show: Data Integrity seals count (with tamper alert if any), HITL review decisions total with pending count, and the Anomaly Trend (total anomalies detected with weekly delta).' },
    ],
  },
  {
    id: 'ai-analysis',
    section: 'Command Center',
    title: 'AI Analysis — Ask Nabhos',
    icon: 'LayoutDashboard',
    keywords: ['ai', 'analysis', 'ask nabhos', 'nlq', 'natural language', 'query', 'chat', 'question', 'answer', 'source', 'mission', 'diagnosis', 'confidence', 'search'],
    blocks: [
      { type: 'p', text: 'AI Analysis (Command Center → AI Analysis, route /analysis) is the Ask Nabhos natural language query workspace. It lets you ask questions about your field data, mission diagnoses, and anomaly records in plain English and get immediate AI-generated answers.' },
      { type: 'h3', text: 'How to use' },
      { type: 'ul', items: [
        'Type a question in the "Ask Anything" search bar — e.g. "What disease was found in the East Block Tea Garden?" or "Which pipeline corridors have unresolved anomalies pending review?"',
        'Press Enter or click "Ask". The system queries your organisation\'s AI provider against your mission analysis data.',
        'The answer appears in a highlighted response panel below the search bar. Source citations (number of mission analysis records used) are shown underneath.',
        'Click "Clear" to reset and ask a new question.',
      ]},
      { type: 'h3', text: 'Example queries' },
      { type: 'p', text: 'Vertical-aware example queries are pre-loaded as clickable chips below the search bar. The set shown depends on your active modules — AgroTech examples for crop/field questions, Oil & Gas examples for pipeline/plume questions.' },
      { type: 'h3', text: 'Stats panel' },
      { type: 'p', text: 'Three summary cards show: total AI Analyses in your organisation, Missions Analysed (drone_survey and ogi_survey missions with AI status "complete"), and Average AI Confidence across all analyses.' },
      { type: 'h3', text: 'Recent AI Analyses list' },
      { type: 'p', text: 'The list below the stats shows up to 10 recent AI analysis records with: analysis type badge, severity badge, confidence %, issue classification text, affected area (ha), and date. Click any row to open the source mission.' },
      { type: 'h3', text: 'Missions with Completed AI Analysis' },
      { type: 'p', text: 'A second list shows drone and OGI survey missions whose AI analysis is complete, with the diagnosis text and confidence. Only appears when such missions exist. Click a row to open the mission detail page.' },
      { type: 'h3', text: 'Fallback mode' },
      { type: 'p', text: 'If no AI provider API key is configured for your organisation, the system uses a data-aware fallback that queries your stored mission analysis records directly without calling an external AI model. An amber warning badge reading "Data-aware mode — Gemini API key not configured" appears below the answer. Configure an API key in Settings → AI Configuration to enable full AI answers.' },
      { type: 'note', text: 'Ask Nabhos answers are generated from your organisation\'s own mission analysis data. It does not have access to data from other organisations. Configure the AI provider in Settings → AI Configuration to get the best results.' },
    ],
  },
  {
    id: 'help-chat',
    section: 'Command Center',
    title: 'Help Chat — AI help assistant',
    icon: 'LayoutDashboard',
    keywords: ['help', 'chat', 'ai', 'assistant', 'question', 'how to', 'guide', 'help chat', 'message', 'minimise', 'slide out'],
    blocks: [
      { type: 'p', text: 'A built-in AI help assistant is available throughout the Help Centre. Click the chat bubble (MessageCircle) button in the top-right corner of any Help page to open it.' },
      { type: 'h3', text: 'What it does' },
      { type: 'p', text: 'The Help Chat answers questions about how to use Nabhos — feature explanations, workflow steps, and navigation guidance. It retrieves the most relevant help articles for your question and uses the configured AI provider to generate a conversational answer.' },
      { type: 'h3', text: 'Controls' },
      { type: 'ul', items: [
        'Type your question and press Enter or click Send.',
        'Click the minimise button (↓) to collapse the chat panel to a header bar — it stays open while you browse articles.',
        'Click the maximise button (↑) to expand it again.',
        'Click × to close the chat entirely.',
      ]},
      { type: 'h3', text: 'Suggested topics' },
      { type: 'p', text: 'When the chat opens, a set of suggested topic chips appears. Click any chip to pre-fill the input with a common question.' },
      { type: 'note', text: 'The Help Chat uses your organisation\'s configured AI provider (Settings → AI Configuration). If no provider is configured, it falls back to a keyword-search-based response using the built-in help articles.' },
    ],
  },

  // ── AgroTech — Fields ───────────────────────────────────────────────────────
  {
    id: 'agrotech-fields',
    section: 'AgroTech — Step 1',
    title: 'Managing fields',
    icon: 'Leaf',
    keywords: ['fields', 'boundaries', 'polygon', 'draw', 'crop', 'geojson', 'import', 'irrigation'],
    blocks: [
      { type: 'p', text: 'The Fields page (Estate Intelligence → Fields) is the starting point for all AgroTech workflows. Every field you create here becomes available for satellite monitoring, anomaly detection, and drone operations.' },
      { type: 'h3', text: 'List view' },
      { type: 'p', text: 'The default view shows all your fields in a table with name, crop type, area (ha), irrigation type, and monitoring status. Use the search bar to filter by name.' },
      { type: 'h3', text: 'Map view' },
      { type: 'p', text: 'Switch to the Map tab to see all field polygons on a Leaflet map, colour-coded by crop type. Click any polygon to open its detail panel.' },
      { type: 'h3', text: 'Creating a field' },
      { type: 'ul', items: [
        'List view: click "New Field" to open the metadata form and draw a boundary.',
        'Map view: click "Draw Field", trace a polygon on the map, then complete the form.',
        'Import: click "Import GeoJSON" and upload a .geojson file to bulk-import field boundaries.',
      ]},
      { type: 'h3', text: 'Metadata form fields' },
      { type: 'table',
        headers: ['Field', 'Required', 'Notes'],
        rows: [
          ['Name', 'Yes', ''],
          ['Crop Type', 'No', 'wheat, rice, maize, soybean, cotton, tea, other'],
          ['Crop Variety', 'No', 'Free text'],
          ['Planting Date', 'No', 'Date picker'],
          ['Irrigation Type', 'No', 'drip, sprinkler, flood, rainfed'],
          ['Area (ha)', 'No', 'Overrides auto-calculated polygon area'],
          ['Monitoring Cadence', 'No', 'How often to auto-check for new imagery'],
          ['Hierarchy Node', 'No', 'Assign to an organisation unit (farm / zone)'],
        ],
      },
      { type: 'h3', text: 'Editing and deleting' },
      { type: 'p', text: 'Click the pencil icon in the list row or the map panel to edit a field. Click the bin icon to delete — a confirmation dialog prevents accidental deletion.' },
    ],
  },

  // ── AgroTech — Temporal Analysis ────────────────────────────────────────────
  {
    id: 'agrotech-temporal',
    section: 'AgroTech — Step 2',
    title: 'Temporal Analysis — fetching satellite imagery',
    icon: 'Leaf',
    keywords: ['temporal', 'satellite', 'imagery', 'ndvi', 'ndre', 'ndwi', 'lst', 'gee', 'fetch', 'cloud', 'date range'],
    blocks: [
      { type: 'p', text: 'Temporal Analysis (Estate Intelligence → Temporal Analysis) fetches Sentinel-2 satellite imagery for a selected field and date range, then displays vegetation index trends over time.' },
      { type: 'h3', text: 'Controls' },
      { type: 'ul', items: [
        'Field — select the field to analyse from the dropdown.',
        'Start Date / End Date — the date range to query. Defaults to the last 90 days. If imagery already exists for this field, clickable "Earliest stored" and "Latest stored" hints appear below the date inputs — click them to snap the range to match the stored data.',
        'Max Cloud Cover — slider from 5% to 80% (default 20%). Only scenes at or below this cloud cover percentage are returned.',
        'Fetch — sends a request to Google Earth Engine to pull new Sentinel-2 imagery for this field and date range.',
      ]},
      { type: 'h3', text: 'Processing banner' },
      { type: 'p', text: 'After clicking Fetch, Google Earth Engine may take 1–3 minutes to process the request. A blue banner appears showing elapsed time. The page automatically checks for new data every 15 seconds for up to 30 minutes. Click "Dismiss" if you want to stop waiting and come back later.' },
      { type: 'h3', text: 'Vegetation index cards' },
      { type: 'p', text: 'Each imagery record shows four index cards:' },
      { type: 'table',
        headers: ['Index', 'Full Name', 'What it measures'],
        rows: [
          ['NDVI', 'Normalised Difference Vegetation Index', 'Overall plant health and green biomass'],
          ['NDRE', 'Normalised Difference Red Edge', 'Chlorophyll content — early stress indicator'],
          ['NDWI', 'Normalised Difference Water Index', 'Canopy water content / irrigation status'],
          ['LST', 'Land Surface Temperature', 'Surface heat — drought and frost indicator'],
        ],
      },
      { type: 'p', text: 'Each card shows the mean, min, max, and standard deviation. The badge colour indicates health: green = normal range, yellow = watch, red = stress.' },
      { type: 'h3', text: 'Time-series table' },
      { type: 'p', text: 'Below the cards, a table lists every imagery record with acquisition date, satellite name, cloud cover %, and all four index values. Rows are sorted newest first.' },
    ],
  },

  // ── AgroTech — Anomaly Alerts ────────────────────────────────────────────────
  {
    id: 'agrotech-anomalies',
    section: 'AgroTech — Steps 3 & 4',
    title: 'Anomaly Alerts & human verification',
    icon: 'Leaf',
    keywords: ['anomaly', 'alert', 'detection', 'confirm', 'reject', 'defer', 'drone inspection', 'ground visit', 'verification', 'human in the loop'],
    blocks: [
      { type: 'p', text: 'The Anomaly Alerts page (Estate Intelligence → Anomaly Alerts) shows satellite-detected anomalies for your AgroTech fields. Step 3 is detection (automatic); Step 4 is your human decision on what to do next.' },
      { type: 'h3', text: 'Stats bar' },
      { type: 'p', text: 'The row at the top shows: total anomalies, critical count, high-severity count, and pending (unreviewed) count. Critical anomalies are highlighted in red.' },
      { type: 'h3', text: 'Filters' },
      { type: 'ul', items: [
        'Field selector — limit the list to a single field.',
        'Severity — critical, high, medium, low.',
        'Status — pending, confirmed, rejected, deferred.',
      ]},
      { type: 'h3', text: 'Run Monitoring' },
      { type: 'p', text: 'The "Run Monitoring" button triggers a fresh satellite scan across all monitored fields. New anomalies appear in the list when the scan completes.' },
      { type: 'h3', text: 'Step 4 — Human Verification (decision gate)' },
      { type: 'p', text: 'Click any anomaly row to open the verification modal. You must choose one of four actions:' },
      { type: 'table',
        headers: ['Action', 'Meaning', 'Next step'],
        rows: [
          ['Confirm → Drone Inspection', 'The anomaly is real — send a drone', 'Creates a drone inspection task; appears in Survey Mission Planner'],
          ['Confirm → Ground Visit', 'The anomaly is real — send a person', 'Creates a ground visit task'],
          ['Reject — False Positive', 'The anomaly is not real', 'Closes the anomaly; no further action'],
          ['Defer', 'Unsure — review later', 'Keeps the anomaly in pending state'],
        ],
      },
      { type: 'p', text: 'You can add optional notes to explain your decision. The decision and notes are saved to the audit log and cannot be changed.' },
    ],
  },

  // ── AgroTech — Survey Mission Planner ───────────────────────────────────────
  {
    id: 'agrotech-survey',
    section: 'AgroTech — Step 5',
    title: 'Survey Mission Planner',
    icon: 'Leaf',
    keywords: ['survey', 'mission', 'planner', 'drone', 'flight', 'altitude', 'overlap', 'weather', 'briefing', 'payload'],
    blocks: [
      { type: 'p', text: 'The Survey Mission Planner (Estate Intelligence → Survey Planner) generates an AI-assisted drone survey plan for a confirmed anomaly. It selects the correct payload, computes flight parameters, and produces a pilot briefing.' },
      { type: 'h3', text: 'Workflow' },
      { type: 'ul', items: [
        '1. Select the confirmed anomaly from the dropdown. Only anomalies you confirmed as "Drone Inspection" in Step 4 appear here.',
        '2. Review the AI-generated survey plan: flight altitude, image overlap %, estimated flight time, grid pattern type, weather constraints, and safety notes.',
        '3. Select the drone asset — choose from your organisation\'s approved drone roster (manufacturer, model, specs).',
        '4. Click "Weather Check" — returns a go / caution / no-go verdict based on current forecast at the field location.',
        '5. Download the pilot briefing document.',
      ]},
      { type: 'h3', text: 'Payload recommendations' },
      { type: 'p', text: 'The system automatically recommends secondary payload options (multispectral, thermal, RGB) based on the anomaly index type. For example, an NDWI irrigation anomaly recommends a thermal camera; an NDVI stress anomaly recommends multispectral.' },
    ],
  },

  // ── AgroTech — Flight Analysis ───────────────────────────────────────────────
  {
    id: 'agrotech-flight',
    section: 'AgroTech — Step 6',
    title: 'Flight Data & Analysis',
    icon: 'Leaf',
    keywords: ['flight', 'analysis', 'upload', 'orthomosaic', 'thermal', 'point cloud', 'scenario', 'validation'],
    blocks: [
      { type: 'p', text: 'After the drone survey, upload the flight data on this page (Estate Intelligence → Flight Analysis) to prepare it for AI processing.' },
      { type: 'h3', text: 'Scenario templates' },
      { type: 'p', text: 'Select the scenario that matches the detected anomaly:' },
      { type: 'ul', items: ['Vegetation Stress', 'Irrigation Fault', 'Pest Infestation', 'Nutrient Deficiency', 'Frost Damage', 'Disease Spread'] },
      { type: 'p', text: 'Each template specifies which file types are required (orthomosaic, thermal layer, point cloud, flight metadata). The page flags any missing files before you can submit.' },
      { type: 'h3', text: 'AI status' },
      { type: 'p', text: 'An AI status badge confirms whether the Nabhos AI engine is ready to process the selected scenario. If the badge shows "Unavailable", check your organisation\'s AI configuration in Settings → AI Configuration.' },
    ],
  },

  // ── AgroTech — Analysis Review ───────────────────────────────────────────────
  {
    id: 'agrotech-review',
    section: 'AgroTech — Step 7',
    title: 'Analysis Review — HITL gate 2',
    icon: 'Leaf',
    keywords: ['analysis review', 'hitl', 'human in the loop', 'confirm diagnosis', 'override', 'ai diagnosis', 'confidence'],
    blocks: [
      { type: 'p', text: 'Analysis Review (Estate Intelligence → Analysis Review) is the second human decision gate. After the AI processes your drone imagery, you must review the diagnosis and either confirm or override it.' },
      { type: 'h3', text: 'Diagnosis cards' },
      { type: 'p', text: 'Each completed analysis has a card showing:' },
      { type: 'ul', items: [
        'NDVI — affected area % vs healthy area %',
        'Thermal anomaly extent',
        'Satellite–drone match confidence',
        'Overall confidence score (0–100 %)',
        'Recommended actions list from the AI',
      ]},
      { type: 'h3', text: 'Your decision' },
      { type: 'table',
        headers: ['Action', 'When to use', 'Effect'],
        rows: [
          ['Confirm AI Diagnosis', 'You agree with the AI\'s findings', 'Approves the recommendation; the system proceeds to generate a remediation plan (Step 8)'],
          ['Override with Judgement', 'You disagree or have additional information', 'Requires a written reason; your alternative diagnosis is recorded and used for remediation planning instead'],
        ],
      },
      { type: 'note', text: 'All decisions are recorded in the immutable audit chain. They cannot be edited or deleted after submission.' },
    ],
  },

  // ── AgroTech — Remediation Missions ─────────────────────────────────────────
  {
    id: 'agrotech-remediation',
    section: 'AgroTech — Step 8',
    title: 'Remediation Missions',
    icon: 'Leaf',
    keywords: ['remediation', 'spray', 'fertiliser', 'treatment', 'compliance', 'approval', 'mission', 'ground team'],
    blocks: [
      { type: 'p', text: 'Once you confirm a diagnosis in Step 7, the system auto-generates a remediation plan on this page (Estate Intelligence → Remediation Missions).' },
      { type: 'h3', text: 'Mission types' },
      { type: 'table',
        headers: ['Type', 'Description'],
        rows: [
          ['Precision Spray', 'Drone-delivered chemical application — includes chemical name, rate (g/L and kg/ha), total volume, and buffer from waterways'],
          ['Fertiliser Application', 'Targeted nutrient delivery to stressed zones'],
          ['Marker Flight', 'Low-altitude drone pass to flag zones for ground teams'],
          ['Ground Team Dispatch', 'Manual intervention instructions for field staff'],
        ],
      },
      { type: 'h3', text: 'Compliance guardrails' },
      { type: 'p', text: 'Regulatory minimums (e.g. buffer distances from water bodies) are pre-filled and locked — they cannot be reduced below the regulatory minimum. Chemical rates are validated against the approved-assets database.' },
      { type: 'h3', text: 'Approval workflow' },
      { type: 'p', text: 'Missions start as "draft". A supervisor reviews and approves them, changing the status to "planned". The mission then appears in Mission Control for pilot dispatch.' },
    ],
  },

  // ── AgroTech — Treatment Efficacy ───────────────────────────────────────────
  {
    id: 'agrotech-efficacy',
    section: 'AgroTech — Step 8.6',
    title: 'Treatment Efficacy',
    icon: 'Leaf',
    keywords: ['efficacy', 'treatment', 're-survey', 'recovery', 'ndvi', 'scoring', 'pre-treatment', 'post-treatment'],
    blocks: [
      { type: 'p', text: 'Treatment Efficacy (Estate Intelligence → Treatment Efficacy) scores how well each completed remediation mission worked. It compares NDVI values before and after treatment to produce a recovery delta and an efficacy index.' },
      { type: 'h3', text: 'Efficacy cards' },
      { type: 'p', text: 'Each remediation mission that has post-treatment satellite imagery gets a card showing:' },
      { type: 'ul', items: [
        'Pre-treatment NDVI — mean value from imagery up to 20 days before the mission date.',
        'Post-treatment NDVI — mean value from imagery taken after treatment.',
        'Recovery delta — the change in NDVI (positive = improvement).',
        'Efficacy score — 0–100 index derived from the recovery delta relative to a target full recovery (NDVI 0.75).',
        'Re-survey status — Scheduled / Complete / Overdue / No data.',
      ]},
      { type: 'h3', text: 'Re-survey due date' },
      { type: 'p', text: 'The re-survey due date is derived from the mission date plus a configurable number of days (default: 14 days). An "Overdue" badge appears if no new imagery has been fetched after the due date.' },
      { type: 'h3', text: 'Interpreting the efficacy score' },
      { type: 'table',
        headers: ['Score range', 'Interpretation'],
        rows: [
          ['80–100', 'Strong recovery — treatment appears highly effective'],
          ['50–79',  'Partial recovery — monitor for a further 14 days before re-treating'],
          ['20–49',  'Weak recovery — consider adjusted treatment or agronomist review'],
          ['0–19',   'No measurable recovery — re-survey data may be insufficient, or treatment failed'],
        ],
      },
    ],
  },

  // ── AgroTech — Operations Dashboard ────────────────────────────────────────
  {
    id: 'agrotech-dashboard',
    section: 'AgroTech — Reports',
    title: 'Operations Dashboard',
    icon: 'Leaf',
    keywords: ['dashboard', 'kpi', 'overview', 'operations', 'estate', 'summary', 'action queue', 'situational awareness'],
    blocks: [
      { type: 'p', text: 'The Estate Intelligence Dashboard (Estate Intelligence → Operations Dashboard) gives a live situational overview of the full 9-step field intelligence loop in a single view — no need to visit each page individually to check for pending actions.' },
      { type: 'h3', text: 'KPI cards' },
      { type: 'table',
        headers: ['Card', 'What it shows', 'Links to'],
        rows: [
          ['Fields Monitored',    'Total active field boundary polygons',                           'Fields'],
          ['Pending Anomalies',   'Anomalies awaiting Step 4 human verification',                   'Anomaly Alerts'],
          ['Active Surveys',      'Drone survey missions currently in-progress or pending upload',   'Flight Data & Analysis'],
          ['Active Remediations', 'Remediation missions currently running',                         'Remediation Missions'],
        ],
      },
      { type: 'h3', text: 'Action Queue' },
      { type: 'p', text: 'The workflow step list shows each of the 9 steps with a count badge where human action is currently needed. Red badges indicate urgent items:' },
      { type: 'ul', items: [
        'Steps 3–4 Anomaly Alerts — anomalies awaiting your Step 4 decision',
        'Step 7 Analysis Review — drone missions with complete AI diagnosis waiting for Agronomist sign-off',
        'Step 8 Remediation Missions — active remediation missions in progress',
      ]},
      { type: 'p', text: 'Click any step row to navigate directly to that page. The amber attention banner at the top shows the total action count across all three gates.' },
    ],
  },

  // ── AgroTech — Reports ───────────────────────────────────────────────────────
  {
    id: 'agrotech-reports',
    section: 'AgroTech — Reports',
    title: 'Crop Health Reports',
    icon: 'Leaf',
    keywords: ['reports', 'export', 'csv', 'audit', 'seasonal', 'health summary', 'ndvi', 'anomaly', 'timeline'],
    blocks: [
      { type: 'p', text: 'Crop Health Reports (Estate Intelligence → Crop Health Reports) compiles real-time field data into three views you can review on-screen and export.' },
      { type: 'h3', text: 'Estate Overview table' },
      { type: 'p', text: 'A table with one row per field showing: latest NDVI, NDVI trend (↑ improving / ↓ declining / → stable), active anomaly count, resolved anomaly count, treatment count, and last satellite imagery date.' },
      { type: 'h3', text: 'Audit Chain Timeline' },
      { type: 'p', text: 'An expandable timeline per anomaly showing the full chain of decisions: satellite detection → Step 4 human decision → remediation mission → efficacy score. Each entry shows actor, timestamp, and decision note.' },
      { type: 'h3', text: 'Seasonal NDVI comparison' },
      { type: 'p', text: 'Side-by-side NDVI means for each field, comparing the current year against the historical baseline. Fields that are declining relative to baseline are highlighted in red.' },
      { type: 'h3', text: 'CSV export' },
      { type: 'p', text: 'Click "Export CSV" at the top of the page to download the estate overview table as a spreadsheet. PDF export is planned for a future release.' },
    ],
  },

  // ── Precision Planning ────────────────────────────────────────────────────────
  {
    id: 'precision-overview',
    section: 'Precision Planning',
    title: 'Precision Planning module overview',
    icon: 'Target',
    keywords: ['precision', 'planning', 'variable rate', 'vra', 'yield', 'suitability', 'land expansion', 'chemical', 'compliance', 'isoxml', 'mvp2'],
    blocks: [
      { type: 'p', text: 'Precision Planning (enabled via Platform Admin → Module Marketplace as "mvp2_agrotech_precision") adds five data-driven agronomic tools on top of the AgroTech satellite and drone data already collected.' },
      { type: 'table',
        headers: ['Page', 'Route', 'Purpose'],
        rows: [
          ['Variable-Rate Scripting',  'Precision Planning → Variable-Rate Scripting',  'Generate per-zone application rate prescriptions from satellite NDVI — export as ISOXML CSV or GeoJSON'],
          ['Yield Forecasting',        'Precision Planning → Yield Forecasting',         'AI seasonal yield performance index per field — NDVI baseline comparison with anomaly and treatment adjustments'],
          ['Crop Suitability Index',   'Precision Planning → Crop Suitability Index',    'Score each field for suitability to grow 7 high-value crops (Kiwi, Avocado, Tea, Coffee, etc.)'],
          ['Land Expansion Modelling', 'Precision Planning → Land Expansion Modelling',  'Site suitability scoring and 10-year NPV proxy for new land acquisitions'],
          ['Chemical Compliance',      'Precision Planning → Chemical Compliance',       'Season dosage ledger with max-rate breach detection, no-spray buffer audit, and pre-harvest interval checks'],
        ],
      },
      { type: 'note', text: 'All five pages derive their data from the satellite imagery, anomaly records, and remediation missions already created in the Estate Intelligence module. No additional data entry is required.' },
    ],
  },
  {
    id: 'precision-vra',
    section: 'Precision Planning',
    title: 'Variable-Rate Scripting',
    icon: 'Target',
    keywords: ['variable rate', 'vra', 'prescription', 'ndvi', 'zones', 'application rate', 'isoxml', 'geojson', 'export', 'fertiliser', 'kg/ha'],
    blocks: [
      { type: 'p', text: 'Variable-Rate Scripting (Precision Planning → Variable-Rate Scripting) reads the latest satellite NDVI for each field and divides it into application zones with different input rates.' },
      { type: 'h3', text: 'How zones are derived' },
      { type: 'table',
        headers: ['NDVI range', 'Zone label', 'Application rate'],
        rows: [
          ['< 0.35',      'High Stress Zone',     'Maximum rate (120 kg/ha default)'],
          ['0.35 – 0.55', 'Moderate Stress Zone', 'Standard rate (90 kg/ha default)'],
          ['> 0.55',      'Healthy Zone',          'Reduced maintenance rate (50 kg/ha default)'],
        ],
      },
      { type: 'h3', text: 'Product defaults' },
      { type: 'p', text: 'The recommended product is matched to the crop type set in the Step 1 field metadata. For example, Tea fields default to Urea (46-0-0), Vineyard fields to NPK 12-12-17. You can override the product name per field.' },
      { type: 'h3', text: 'Exports' },
      { type: 'ul', items: [
        '"Export ISOXML CSV" — downloads a CSV structured for import into precision agriculture machinery terminals.',
        '"Export GeoJSON" — downloads a GeoJSON FeatureCollection with zone polygons and rate properties for GIS use.',
      ]},
      { type: 'note', text: 'Zone polygons in the current release are computed from the full field boundary divided by area proportion. Sub-zone polygon geometry from drone multispectral analysis will refine these boundaries in a future release.' },
    ],
  },
  {
    id: 'precision-yield',
    section: 'Precision Planning',
    title: 'Yield Forecasting',
    icon: 'Target',
    keywords: ['yield', 'forecast', 'prediction', 'ndvi', 'baseline', 'trend', 'anomaly penalty', 'remediation', 'performance', 'index', 'sparkline'],
    blocks: [
      { type: 'p', text: 'Yield Forecasting (Precision Planning → Yield Forecasting) generates a 0–100 Projected Yield Index for every field, ranked by trend.' },
      { type: 'h3', text: 'Index methodology' },
      { type: 'p', text: 'The index is computed from three factors:' },
      { type: 'table',
        headers: ['Factor', 'Weight / effect', 'Source'],
        rows: [
          ['NDVI current vs. baseline', 'Core score: (mean NDVI ÷ 0.8) × 100', 'All stored satellite imagery for this field'],
          ['Anomaly penalty',           '−2 to −15 points per unresolved anomaly (by severity)', 'Active anomaly records'],
          ['Remediation boost',         '+2 per completed treatment, capped at +5', 'Completed remediation missions'],
        ],
      },
      { type: 'h3', text: 'Interpreting the score' },
      { type: 'table',
        headers: ['Index range', 'Status'],
        rows: [
          ['70–100', 'High potential'],
          ['45–69',  'Moderate'],
          ['< 45',   'At-risk'],
        ],
      },
      { type: 'h3', text: 'Trend direction' },
      { type: 'p', text: 'The trend badge (Improving / Declining / Stable) is derived by comparing the last two NDVI readings for the field. Fields are sorted by trend — Declining fields appear first so they receive the most attention.' },
      { type: 'h3', text: 'NDVI sparkline' },
      { type: 'p', text: 'Each card shows a small SVG sparkline of the field\'s NDVI history. Green = healthy range, amber = moderate stress, red = severe stress.' },
    ],
  },
  {
    id: 'precision-suitability',
    section: 'Precision Planning',
    title: 'Crop Suitability Index',
    icon: 'Target',
    keywords: ['crop suitability', 'kiwi', 'avocado', 'tea', 'coffee', 'vineyard', 'transition', 'score', 'ndvi', 'ndre', 'ndwi', 'factors'],
    blocks: [
      { type: 'p', text: 'Crop Suitability Index (Precision Planning → Crop Suitability Index) scores each field against seven high-value crops to help identify the best candidate for a crop-type change or expansion.' },
      { type: 'h3', text: 'Crops scored' },
      { type: 'p', text: 'Kiwi, Avocado, Tea, Coffee, Vineyard, Macadamia, Blueberry.' },
      { type: 'h3', text: 'Scoring factors' },
      { type: 'table',
        headers: ['Factor', 'Weight', 'Source'],
        rows: [
          ['Canopy vigour (NDVI)',      '40%', 'Satellite mean NDVI vs. crop minimum and ideal thresholds'],
          ['Nitrogen status (NDRE)',    '20%', 'Satellite mean NDRE vs. crop minimum requirement'],
          ['Soil moisture (NDWI)',      '25%', 'Satellite mean NDWI vs. crop moisture range (min–max)'],
          ['Field health (anomaly rate)','15%', 'Historical anomaly count per hectare'],
          ['Crop family bonus',         '+5 pts', 'Applied if the current crop type is in the same plant family'],
        ],
      },
      { type: 'h3', text: 'Suitability tiers' },
      { type: 'table',
        headers: ['Score', 'Tier'],
        rows: [
          ['≥ 70', 'High'],
          ['50–69', 'Moderate'],
          ['30–49', 'Marginal'],
          ['< 30',  'Unsuitable'],
        ],
      },
      { type: 'note', text: 'The model does not include soil laboratory data or elevation. It is designed as a first-pass satellite-derived screening tool. Confirm with on-ground soil tests before making planting decisions.' },
    ],
  },
  {
    id: 'precision-land',
    section: 'Precision Planning',
    title: 'Land Expansion Modelling',
    icon: 'Target',
    keywords: ['land', 'expansion', 'acquisition', 'site suitability', 'npv', 'roi', 'payback', 'capex', 'opex', 'discount rate', 'ndvi', 'model parameters'],
    blocks: [
      { type: 'p', text: 'Land Expansion Modelling (Precision Planning → Land Expansion Modelling) ranks all defined boundaries by site suitability for a new crop and computes a 10-year NPV proxy to support acquisition decisions.' },
      { type: 'h3', text: 'Suitability score' },
      { type: 'p', text: 'Each site is scored 0–100 from NDVI (50%), NDRE (30%), and NDWI (20%) mean values. Sites are ranked: Excellent → Good → Fair → Poor.' },
      { type: 'h3', text: 'NPV model parameters' },
      { type: 'p', text: 'Click "NPV Model Parameters" to expand the input panel. All values are editable:' },
      { type: 'table',
        headers: ['Parameter', 'Default', 'Notes'],
        rows: [
          ['Target crop',      'Kiwi',   'Used for labelling only — does not change the NDVI weighting'],
          ['Price (currency/kg)', '2.50', 'Farmgate price per kg of harvested produce'],
          ['Yield (kg/ha)',    '15,000',  'Expected annual yield per hectare at full production'],
          ['Capex (currency/ha)', '8,000', 'One-time establishment cost per hectare'],
          ['Opex/yr (currency/ha)', '2,000', 'Annual operating cost per hectare'],
          ['Discount rate (%)', '8',     'WACC or hurdle rate for the NPV calculation'],
          ['Horizon (years)',   '10',    'Investment appraisal period'],
        ],
      },
      { type: 'p', text: 'The currency symbol shown matches the base currency configured for your organisation in Settings → Organisation.' },
      { type: 'note', text: 'NPV proxy is a directional screening tool only. It excludes land title costs, regulatory approval timelines, infrastructure, and climate risk. All parameters are editable — adjust to match actual feasibility study assumptions.' },
    ],
  },
  {
    id: 'precision-compliance',
    section: 'Precision Planning',
    title: 'Chemical Compliance',
    icon: 'Target',
    keywords: ['chemical', 'compliance', 'dosage', 'season', 'max rate', 'breach', 'warning', 'buffer', 'phi', 'pre-harvest interval', 'ledger', 'export', 'csv'],
    blocks: [
      { type: 'p', text: 'Chemical Compliance (Precision Planning → Chemical Compliance) is an automatic dosage audit derived from all completed Precision Spray and Fertiliser remediation missions. It runs three compliance checks per chemical.' },
      { type: 'h3', text: 'Three compliance checks' },
      { type: 'table',
        headers: ['Check', 'Trigger', 'Severity'],
        rows: [
          ['Maximum season dosage', 'Season average application rate exceeds the configured limit for that chemical', 'BREACH (red)'],
          ['No-spray buffer',       'Any application recorded with a buffer-from-waterways value below 10 m',           'WARNING (amber)'],
          ['Pre-harvest interval',  'Last application date is within the PHI window for that chemical',                  'WARNING (amber)'],
        ],
      },
      { type: 'h3', text: 'Status badges' },
      { type: 'ul', items: [
        'COMPLIANT (green) — all three checks pass.',
        'WARNING (amber) — at least one check flagged; no hard dosage breach.',
        'BREACH (red) — season average rate exceeds the configured maximum; requires immediate agronomist review.',
      ]},
      { type: 'h3', text: 'Application log' },
      { type: 'p', text: 'Click "Application log" on any chemical card to expand a table listing every individual spray application with mission name, date, area treated, volume (L), rate (kg/ha), and buffer distance.' },
      { type: 'h3', text: 'CSV export' },
      { type: 'p', text: 'Click "Export CSV" to download the full compliance summary including status (COMPLIANT / WARNING / BREACH) and all recorded dosage figures.' },
      { type: 'h3', text: 'Threshold configuration' },
      { type: 'p', text: 'Default maximum season dosage limits are set per chemical (e.g. Glyphosate: 5 kg/ha, Carbendazim: 2 kg/ha). Organisation Admins can override these in Settings → Approved Assets. The PHI window and minimum buffer distance are currently fixed at platform level.' },
      { type: 'note', text: 'Compliance data only appears here after remediation missions with spray metadata are recorded in Step 8. Missions with no spray data (e.g. ground team dispatch, marker flights) are excluded from the ledger.' },
    ],
  },

  // ── Oil & Gas ─────────────────────────────────────────────────────────────────
  {
    id: 'og-overview',
    section: 'Oil & Gas',
    title: 'Oil & Gas module overview',
    icon: 'Flame',
    keywords: ['oil', 'gas', 'pipeline', 'ogi', 'methane', 'corridor', 'rrow', 'right-of-way', 'thermal', 'leak', 'encroachment', 'mvp3'],
    blocks: [
      { type: 'p', text: 'The Oil & Gas module (enabled via Platform Admin → Module Marketplace) delivers the full satellite-to-drone-to-incident-response intelligence loop for pipeline corridor monitoring, OGI methane leak detection, and upstream exploration.' },
      { type: 'h3', text: 'Eight-step workflow' },
      { type: 'table',
        headers: ['Step', 'Page', 'Who acts'],
        rows: [
          ['1 — Corridor Boundaries',   'Oil & Gas → Corridor Monitoring → Corridor Boundaries',   'Pipeline Integrity Manager'],
          ['2 — Temporal Analysis',     'Oil & Gas → Corridor Monitoring → Temporal Analysis',     'Pipeline Integrity Manager'],
          ['3–4 — Anomaly Alerts',      'Oil & Gas → Corridor Monitoring → Anomaly Alerts',        'Pipeline Integrity Manager'],
          ['5 — OGI Survey Planner',    'Oil & Gas → Drone Operations → Survey Mission Planner',   'OGI Drone Technician'],
          ['6 — Flight Data & Analysis','Oil & Gas → Drone Operations → Flight Data & Analysis',   'OGI Drone Technician'],
          ['7 — Analysis Review',       'Oil & Gas → Drone Operations → Analysis Review',          'HSSE Safety Officer'],
          ['8a — Response Planning',    'Oil & Gas → Incident Response → Response Planning',       'Pipeline Integrity Manager'],
          ['8b — Incident Closure',     'Oil & Gas → Incident Response → Incident Closure',        'Pipeline Integrity Manager'],
        ],
      },
      { type: 'h3', text: 'Additional pages' },
      { type: 'table',
        headers: ['Page', 'Location', 'Purpose'],
        rows: [
          ['Operations Dashboard',    'Oil & Gas → Operations Dashboard',                          'Cross-step KPI summary and action queue for all 8 steps'],
          ['Upstream Exploration',    'Oil & Gas → Upstream Exploration',                          'Aeromagnetic, hyperspectral, and seismic survey management'],
          ['Plume & Leak Analyzer',   'Oil & Gas → Deep Analysis → Plume & Leak Analyzer',        'CH₄ flow-rate quantification from completed OGI missions'],
          ['Facility Safety & Flaring','Oil & Gas → Deep Analysis → Facility Safety & Flaring',   'Thermal baseline monitoring and flare intensity scoring'],
          ['ERP Integration Hub',     'Oil & Gas → Incident Management → ERP Integration Hub',    'SAP/Oracle work order audit trail from Step 8a dispatches'],
          ['Value Realization & ROI', 'Oil & Gas → Incident Management → Value Realization & ROI','Product loss, fine avoidance, and CO₂-equivalent ROI metrics'],
          ['Regulatory Reporting',    'Oil & Gas → Incident Management → Regulatory Reporting',   'SHA-256 sealed evidence packs for DPR/NUPRC, EPA, or internal audit'],
        ],
      },
      { type: 'note', text: 'The module must be enabled for your organisation in the Module Marketplace before the Oil & Gas navigation section appears.' },
    ],
  },
  {
    id: 'og-boundaries',
    section: 'Oil & Gas',
    title: 'Step 1 — Corridor Boundaries',
    icon: 'Flame',
    keywords: ['corridor', 'boundary', 'pipeline id', 'right-of-way', 'row', 'buffer', 'product type', 'operator', 'monitoring frequency'],
    blocks: [
      { type: 'p', text: 'The Corridor Boundaries page registers your pipeline right-of-way (RoW) polygons. Each corridor record is the spatial container that drives all downstream satellite monitoring and anomaly detection for that pipeline segment.' },
      { type: 'h3', text: 'Creating a corridor' },
      { type: 'p', text: 'Click "Add Corridor" and complete the form:' },
      { type: 'table',
        headers: ['Field', 'Required', 'Notes'],
        rows: [
          ['Corridor Name', 'Yes', 'Descriptive name, e.g. "Escravos–Warri Trunk Line"'],
          ['Pipeline ID', 'Yes', 'Your asset register ID, e.g. "PL-EWT-001"'],
          ['Product Type', 'No', 'Crude oil, natural gas, refined products, condensate, produced water'],
          ['Buffer Width (m)', 'No', 'RoW buffer in metres — defaults to 100 m'],
          ['Operator / Company', 'No', 'e.g. NNPC, Shell, TotalEnergies'],
          ['Monitoring Frequency', 'No', 'Daily, weekly, bi-weekly, or monthly satellite check'],
        ],
      },
      { type: 'p', text: 'Once saved, the corridor appears in the Temporal Analysis corridor selector and the anomaly filter dropdown.' },
      { type: 'note', text: 'Each corridor is tagged with vertical = "oil_gas" so it is kept separate from AgroTech field boundaries throughout the platform.' },
    ],
  },
  {
    id: 'og-temporal',
    section: 'Oil & Gas',
    title: 'Step 2 — Temporal Analysis',
    icon: 'Flame',
    keywords: ['temporal', 'satellite', 'imagery', 'lst', 'ndvi', 'ndbi', 'ndwi', 'die-off', 'gee', 'fetch', 'cloud cover', 'thermal'],
    blocks: [
      { type: 'p', text: 'The Temporal Analysis page fetches Sentinel-2 satellite imagery for a selected pipeline corridor and displays four indices over time that are relevant to pipeline integrity.' },
      { type: 'h3', text: 'Satellite indices explained' },
      { type: 'table',
        headers: ['Index', 'Full Name', 'Why it matters for pipelines'],
        rows: [
          ['NDVI', 'Normalised Difference Vegetation Index', 'Vegetation die-off along RoW corridor is the primary indicator of a subsurface hydrocarbon leak'],
          ['LST',  'Land Surface Temperature', 'Thermal anomalies above baseline may indicate product migration or compressor heat'],
          ['NDBI', 'Normalised Difference Built-up Index', 'Detects heavy machinery and new construction encroaching on the RoW'],
          ['NDWI', 'Normalised Difference Water Index', 'Water body changes or soil moisture anomalies near pipeline crossings'],
        ],
      },
      { type: 'h3', text: 'How to use' },
      { type: 'ul', items: [
        'Select a corridor and set a date range (default: last 90 days).',
        'Click "Fetch Imagery" — the request is queued with Google Earth Engine (GEE). Allow 1–3 minutes.',
        'Results appear as a table with one row per cloud-free acquisition.',
        'Scenes with > 20% cloud cover over the corridor are automatically excluded.',
      ]},
    ],
  },
  {
    id: 'og-anomalies',
    section: 'Oil & Gas',
    title: 'Steps 3 & 4 — Anomaly Alerts & verification',
    icon: 'Flame',
    keywords: ['anomaly', 'alert', 'pipeline', 'leak', 'encroachment', 'thermal spike', 'die-off', 'confirm', 'reject', 'defer', 'ogi survey', 'step 4', 'human verification'],
    blocks: [
      { type: 'p', text: 'The Anomaly Alerts page shows satellite-detected anomalies for your pipeline corridors. Step 3 is automatic AI detection; Step 4 is your human decision on each alert.' },
      { type: 'h3', text: 'Anomaly types' },
      { type: 'ul', items: [
        'Vegetation die-off — NDVI drop along RoW, primary leak indicator',
        'Thermal spike — LST anomaly above corridor baseline',
        'NDBI increase — built-up index rise indicating machinery / construction encroachment',
        'NDWI change — water-body or soil-moisture anomaly near river crossings',
      ]},
      { type: 'h3', text: 'Step 4 — Human verification' },
      { type: 'p', text: 'Click "Review" on any pending anomaly to open the decision modal:' },
      { type: 'table',
        headers: ['Decision', 'Meaning', 'Next step'],
        rows: [
          ['Confirm — Schedule OGI Drone Survey', 'Anomaly is real — dispatch OGI drone', 'Creates an ogi_survey mission; appears in Step 5'],
          ['Confirm — Ground Team Inspection', 'Anomaly is real — ground patrol only', 'Ground visit task created; no drone required'],
          ['Reject — False Positive', 'Satellite artefact or seasonal variation', 'Anomaly closed — no further action'],
          ['Defer — Monitor Next Pass', 'Inconclusive — wait for next satellite pass', 'Anomaly stays pending'],
        ],
      },
      { type: 'note', text: 'Critical anomalies (red rows) should be reviewed within 24 hours. Use "Run Monitoring" to trigger an immediate re-scan across all O&G corridors.' },
    ],
  },
  {
    id: 'og-survey',
    section: 'Oil & Gas',
    title: 'Step 5 — OGI Survey Mission Planner',
    icon: 'Flame',
    keywords: ['ogi', 'optical gas imaging', 'survey', 'planner', 'payload', 'flir', 'altitude', 'wind', 'weather', 'briefing', 'low altitude'],
    blocks: [
      { type: 'p', text: 'The OGI Survey Mission Planner generates an AI-derived flight plan for a confirmed corridor anomaly. It selects the correct OGI or thermal payload and computes safe flight parameters for low-altitude pipeline inspection.' },
      { type: 'h3', text: 'AI-derived parameters' },
      { type: 'table',
        headers: ['Parameter', 'OGI (leak/methane)', 'Thermal (hotspot)'],
        rows: [
          ['Primary payload',   'FLIR GF320 OGI Camera',      'FLIR Vue Pro R Thermal (LWIR)'],
          ['Survey altitude',   '30 m AGL',                   '50 m AGL'],
          ['Max wind speed',    '3 m/s',                      '5 m/s'],
          ['Illumination',      'Avoid direct sunlight glare', 'Fly within 2 hr of sunrise/sunset'],
        ],
      },
      { type: 'h3', text: 'Creating a survey mission' },
      { type: 'ul', items: [
        'Select the confirmed anomaly from the list.',
        'Review the AI-generated plan (payload, altitude, speed, weather constraints, safety notes).',
        'Enter a mission name and optional pilot notes.',
        'Click "Create Mission" — the survey mission is created and linked to the anomaly.',
        'Upload OGI flight data in Step 6 after the survey is flown.',
      ]},
      { type: 'note', text: 'Always notify the pipeline control room before flight. Do not fly directly over a suspected active release point.' },
    ],
  },
  {
    id: 'og-flight',
    section: 'Oil & Gas',
    title: 'Step 6 — Flight Data & Analysis',
    icon: 'Flame',
    keywords: ['ogi', 'thermal', 'upload', 'radiometric', 'flight log', 'telemetry', 'diagnosis', 'plume', 'hotspot', 'analysis status'],
    blocks: [
      { type: 'p', text: 'After the OGI or thermal drone survey is flown, upload the flight data here. The Nabhos AI engine processes the OGI frames to detect gas plumes and thermal hotspots, returning a diagnosis and plume emission estimate.' },
      { type: 'h3', text: 'Required file types' },
      { type: 'table',
        headers: ['File type', 'Format', 'Required'],
        rows: [
          ['OGI Radiometric Video', 'RJPEG / SER / MP4 (FLIR GF-series)', 'Yes'],
          ['Thermal Orthomosaic',   'GeoTIFF (radiometric)',               'Yes'],
          ['Flight Log / Telemetry','CSV / KML / GPX',                    'Yes'],
          ['Pre-flight Checklist',  'PDF',                                 'No'],
        ],
      },
      { type: 'p', text: 'Click "Upload Data" on the mission row to go to the Mission Ingestion page. Once uploaded, AI processing starts automatically. The analysis status badge updates to "Analysis Complete" when ready.' },
      { type: 'h3', text: 'AI diagnosis' },
      { type: 'p', text: 'Expand a mission row to see: diagnosis text, severity, confidence %, plume emission rate (kg/hr), and thermal anomaly (°C above baseline). The Safety Officer reviews this in Step 7.' },
    ],
  },
  {
    id: 'og-review',
    section: 'Oil & Gas',
    title: 'Step 7 — Analysis Review (Safety Officer)',
    icon: 'Flame',
    keywords: ['analysis review', 'safety officer', 'hsse', 'sign-off', 'confirm', 'override', 'shut-in', 'step 7', 'ogi diagnosis', 'plume rate'],
    blocks: [
      { type: 'p', text: 'The Analysis Review page is the HSSE Safety Officer\'s decision gate. No incident response can be triggered until a qualified reviewer records a Step 7 decision here.' },
      { type: 'h3', text: 'Decision options' },
      { type: 'table',
        headers: ['Decision', 'When to use', 'Effect'],
        rows: [
          ['Confirm Critical — Immediate Shut-in', 'Active release confirmed, safety risk present', 'Escalates to Step 8; operator initiates shut-in procedure'],
          ['Confirm — Elevated Monitoring', 'Release likely but not yet critical', 'Escalates to Step 8; enhanced monitoring required'],
          ['Override — Downgrade', 'AI overstated severity based on field context', 'Downgraded priority; kept for monitoring'],
          ['Reject — False Positive', 'No gas release on ground inspection evidence', 'Mission closed; no response triggered'],
        ],
      },
      { type: 'note', text: 'All decisions are recorded in the immutable audit chain with timestamp and officer identity. They cannot be changed after submission.' },
    ],
  },
  {
    id: 'og-response',
    section: 'Oil & Gas',
    title: 'Step 8a — Response Planning & ERP work orders',
    icon: 'Flame',
    keywords: ['response', 'planning', 'ground team', 'dispatch', 'pressure test', 'shut-in', 'erp', 'sap', 'oracle', 'maximo', 'work order', 'team lead'],
    blocks: [
      { type: 'p', text: 'Response Planning lets you assign a response team, select the response procedure, and log the ERP work order reference — all in one action.' },
      { type: 'h3', text: 'Response types' },
      { type: 'ul', items: [
        'Ground Inspection — surface patrol of the affected RoW segment',
        'Pressure Test — controlled pressure integrity test on the isolated segment',
        'Shut-in Procedure — valve isolation of the affected segment',
        'Emergency Repair — immediate repair crew mobilisation',
        'Leak Containment — containment boom / absorbent deployment',
      ]},
      { type: 'h3', text: 'ERP integration' },
      { type: 'p', text: 'Enter your ERP system (SAP / Oracle EBS / Maximo / SAP S/4HANA) and work order reference. These are stored against the mission record and appear in the Incident Closure audit trail.' },
      { type: 'p', text: 'Click "Dispatch Team" to record the dispatch. The incident moves immediately to Incident Closure (Step 8b).' },
    ],
  },
  {
    id: 'og-closure',
    section: 'Oil & Gas',
    title: 'Step 8b — Incident Closure & SHA-256 audit seal',
    icon: 'Flame',
    keywords: ['incident', 'closure', 'sha-256', 'hash', 'audit', 'regulatory', 'dpr', 'nuprc', 'seal', 'repair', 'product loss', 'tamper proof'],
    blocks: [
      { type: 'p', text: 'Incident Closure is the final step. Completing this generates a SHA-256 integrity hash that cryptographically seals the full evidence chain from satellite detection through to remediation outcome.' },
      { type: 'h3', text: 'Closure form fields' },
      { type: 'table',
        headers: ['Field', 'Required', 'Notes'],
        rows: [
          ['Closure Outcome', 'Yes', 'Repaired / Shut-in / False Alarm / Monitoring Only'],
          ['Product Loss (barrels)', 'No', 'Volume of hydrocarbon released; enter 0 if none'],
          ['Repair Method', 'No', 'e.g. composite sleeve, weld repair, clamp'],
          ['Regulatory Body Notified', 'No', 'Tick if DPR/NUPRC or relevant authority was informed'],
          ['Closure Notes', 'No', 'Root cause, lessons learned, follow-up actions'],
        ],
      },
      { type: 'h3', text: 'SHA-256 seal' },
      { type: 'p', text: 'On submission, a SHA-256 hash is computed from the closure record and stored against the mission. This hash is displayed in the Closed Incidents audit trail panel and can be shared with regulators or auditors as a tamper-proof record.' },
      { type: 'note', text: 'Keep a copy of the SHA-256 hash for DPR/NUPRC, EPA, or internal compliance submissions. Once generated it cannot be altered.' },
    ],
  },
  {
    id: 'og-exploration',
    section: 'Oil & Gas',
    title: 'Upstream Exploration',
    icon: 'Flame',
    keywords: ['upstream', 'exploration', 'magnetometry', 'aeromagnetic', 'hyperspectral', 'seismic', 'gravity', 'prospect', 'basin', 'viability', 'de-risk'],
    blocks: [
      { type: 'p', text: 'The Upstream Exploration page manages aeromagnetic, hyperspectral, seismic, and gravity survey blocks for prospect de-risking before committing to full seismic campaigns.' },
      { type: 'h3', text: 'Registering a survey' },
      { type: 'ul', items: [
        'Click "New Survey" and fill in: survey name, type, basin name, prospect name (optional), area (km²), and target depth (m).',
        'Supported survey types: Aeromagnetic, Hyperspectral Imaging, Seismic 2D, Seismic 3D, Gravity.',
        'Once registered, upload survey data via the Mission Ingestion page to trigger AI structural interpretation.',
      ]},
      { type: 'h3', text: 'AI viability score' },
      { type: 'p', text: 'When AI analysis is complete, a viability score (0–100) is shown on the survey card:' },
      { type: 'table',
        headers: ['Score range', 'Colour', 'Interpretation'],
        rows: [
          ['70–100', 'Green',  'Viable prospect — recommend further evaluation'],
          ['40–69',  'Amber',  'Uncertain — additional data collection advised'],
          ['0–39',   'Red',    'Non-viable — de-prioritise for current campaign'],
        ],
      },
    ],
  },

  {
    id: 'og-plume',
    section: 'Oil & Gas',
    title: 'Plume & Leak Analyzer',
    icon: 'Flame',
    keywords: ['plume', 'leak', 'methane', 'ch4', 'ogi', 'flow rate', 'column density', 'kg/hr', 'stanford mass balance', 'dispersion', 'source location', 'thermal anomaly'],
    blocks: [
      { type: 'p', text: 'The Plume & Leak Analyzer (Oil & Gas → Deep Analysis → Plume & Leak Analyzer) aggregates CH₄ telemetry from all completed OGI survey missions that have plume data in their AI analysis results.' },
      { type: 'h3', text: 'What it shows' },
      { type: 'table',
        headers: ['Metric', 'Description'],
        rows: [
          ['CH₄ Flow Rate (kg/hr)', 'Estimated volumetric methane leak rate using the Stanford mass-balance method applied to OGI radiometric frames'],
          ['Column Density (ppm)', 'Mean CH₄ column concentration derived from OGI frame radiometry'],
          ['Peak Concentration (ppm)', 'Highest single-frame CH₄ reading from the survey flight'],
          ['Thermal Anomaly (°C)', 'Temperature above corridor baseline — corroborates gas release evidence'],
          ['Plume Source (GPS)', 'AI-localised source coordinates and dispersion radius (m)'],
        ],
      },
      { type: 'h3', text: 'Flow-rate bar colour coding' },
      { type: 'ul', items: [
        'Green — < 10 kg/hr (minor, monitor)',
        'Amber — 10–40 kg/hr (moderate, elevated priority)',
        'Orange — 40–100 kg/hr (high, dispatch response)',
        'Red — > 100 kg/hr (critical, immediate shut-in consideration)',
      ]},
      { type: 'note', text: 'Data only appears here after an OGI mission has AI analysis status "complete" and has plume_rate_kg_hr populated in its metadata. Run the full Steps 5–7 workflow to generate this data.' },
    ],
  },
  {
    id: 'og-facility-safety',
    section: 'Oil & Gas',
    title: 'Facility Safety & Flaring',
    icon: 'Flame',
    keywords: ['facility', 'safety', 'flaring', 'thermal', 'baseline', 'flare intensity', 'radiative power', 'asset', 'wellhead', 'storage tank', 'compressor', 'flare stack'],
    blocks: [
      { type: 'p', text: 'The Facility Safety & Flaring page (Oil & Gas → Deep Analysis → Facility Safety) monitors thermal events across your asset base. It reads completed OGI/thermal survey missions that have thermal anomaly or flare data.' },
      { type: 'h3', text: 'Asset types tracked' },
      { type: 'ul', items: ['Wellhead', 'Storage Tank', 'Compressor Station', 'Pipeline', 'Flare Stack'] },
      { type: 'h3', text: 'Key metrics' },
      { type: 'table',
        headers: ['Metric', 'Description'],
        rows: [
          ['Thermal Anomaly (°C)', 'Temperature above the established corridor baseline — the primary thermal event indicator'],
          ['Baseline Temp (°C)', 'Facility-specific thermal baseline established from historical survey data'],
          ['Peak Temp (°C)', 'Maximum temperature recorded during the survey flight'],
          ['Flare Radiative Power (MW)', 'Radiative power from flare stacks derived from thermal bands'],
          ['Flare Intensity Score (0–100)', 'AI-normalised composite score: ≥ 80 = Severe, 60–79 = High, 30–59 = Moderate, < 30 = Normal'],
        ],
      },
      { type: 'h3', text: 'Asset Safety Heatmap' },
      { type: 'p', text: 'The heatmap panel groups thermal events by asset type so you can see which asset class is generating the most alerts across your corridors at a glance.' },
    ],
  },
  {
    id: 'og-erp',
    section: 'Oil & Gas',
    title: 'ERP Integration Hub',
    icon: 'Flame',
    keywords: ['erp', 'sap', 'oracle', 'maximo', 'work order', 'dispatch', 'audit trail', 'integration', 'connector', 'status'],
    blocks: [
      { type: 'p', text: 'The ERP Integration Hub (Oil & Gas → Incident Management → ERP Integration) surfaces every ERP work order reference captured during Response Planning (Step 8a) in a single searchable audit table.' },
      { type: 'h3', text: 'Supported ERP systems' },
      { type: 'ul', items: ['SAP PM', 'SAP S/4HANA', 'Oracle EBS', 'Maximo'] },
      { type: 'h3', text: 'Work order status lifecycle' },
      { type: 'table',
        headers: ['Status', 'Meaning'],
        rows: [
          ['Dispatched', 'Response team dispatched; work order reference recorded; ground team active'],
          ['Closed',     'Incident has been closed and SHA-256 sealed in Incident Closure (Step 8b)'],
          ['Pending',    'Work order reference exists but no dispatch timestamp yet recorded'],
        ],
      },
      { type: 'h3', text: 'Audit trail' },
      { type: 'p', text: 'The dispatch audit table records: incident name, corridor, ERP system, work order reference, response type, team lead, dispatch timestamp, current status, and the SHA-256 closure hash (truncated). This table is the primary source for ERP integration audits.' },
      { type: 'note', text: 'Live SAP/Oracle connector with automatic work order creation and status polling is planned for MVP 3.5. Current references are recorded manually during Response Planning.' },
    ],
  },
  {
    id: 'og-value',
    section: 'Oil & Gas',
    title: 'Value Realization & ROI',
    icon: 'Flame',
    keywords: ['roi', 'value', 'realization', 'product loss', 'barrels', 'fine avoidance', 'emissions', 'co2', 'carbon', 'esg', 'return on investment', 'savings'],
    blocks: [
      { type: 'p', text: 'The Value Realization & ROI page (Oil & Gas → Incident Management → Value Realization) computes the commercial and environmental value Nabhos has delivered by detecting pipeline incidents early. All figures are derived from closed incident records.' },
      { type: 'h3', text: 'Four headline metrics' },
      { type: 'table',
        headers: ['Metric', 'How it is calculated'],
        rows: [
          ['Intercepted Product Value', 'Average 5 bbl saved per repaired incident × crude price ($/bbl)'],
          ['Regulatory Fine Avoidance', 'Regulatory fine estimate per notified incident × count of regulatory-reported incidents'],
          ['CO₂-Equivalent Prevented', 'Detected CH₄ kg × IPCC AR6 GWP-100 factor (29.8 kg CO₂e / kg CH₄)'],
          ['Return on Detection', 'Total intercepted value ÷ estimated survey cost per OGI mission'],
        ],
      },
      { type: 'h3', text: 'Adjusting the model' },
      { type: 'p', text: 'The default constants (crude price, fine estimate, survey cost) are indicative figures. Your platform administrator can update the constants in the source to match your jurisdiction, commodity price, and operational cost structure.' },
      { type: 'note', text: 'Data only appears on this page after incidents have been closed in Incident Closure (Step 8b). The closure form captures product loss volume, regulatory notification status, and plume rate — all inputs to the ROI model.' },
    ],
  },

  {
    id: 'og-dashboard',
    section: 'Oil & Gas',
    title: 'Operations Dashboard',
    icon: 'Flame',
    keywords: ['dashboard', 'kpi', 'overview', 'operations', 'pipeline', 'summary', 'action queue', 'situational awareness'],
    blocks: [
      { type: 'p', text: 'The Operations Dashboard (Oil & Gas → Operations Dashboard) gives a live situational overview of the full 8-step intelligence loop in a single view — no need to visit each page individually to check for pending actions.' },
      { type: 'h3', text: 'KPI cards' },
      { type: 'table',
        headers: ['Card', 'What it shows', 'Links to'],
        rows: [
          ['Corridors Monitored', 'Total active O&G pipeline corridor polygons', 'Corridor Boundaries'],
          ['Pending Anomalies',   'Anomalies awaiting Step 4 human verification', 'Anomaly Alerts'],
          ['Active OGI Surveys',  'Missions currently in-progress or pending data upload', 'Flight Data & Analysis'],
          ['Closed This Month',   'Incidents sealed in the current calendar month', 'Incident Closure'],
          ['Plume Surveys',       'OGI missions with plume rate data confirmed', 'Plume & Leak Analyzer'],
        ],
      },
      { type: 'h3', text: 'Action Queue' },
      { type: 'p', text: 'The workflow step list shows each of the 8 steps with a count badge where human action is currently needed. Red badges indicate urgent items:' },
      { type: 'ul', items: [
        'Steps 3–4 Anomaly Alerts — anomalies awaiting your Step 4 decision',
        'Step 7 Analysis Review — OGI missions with complete AI diagnosis waiting for Safety Officer sign-off',
        'Step 8a Response Planning — confirmed incidents awaiting response team dispatch',
      ]},
      { type: 'p', text: 'Click any step row to navigate directly to that page. The amber attention banner at the top shows the total action count across all three gates.' },
    ],
  },
  {
    id: 'og-regulatory',
    section: 'Oil & Gas',
    title: 'Regulatory Reporting',
    icon: 'Flame',
    keywords: ['regulatory', 'reporting', 'dpr', 'nuprc', 'epa', 'compliance', 'evidence', 'sha-256', 'audit', 'submission', 'csv', 'export', 'print', 'chain of custody'],
    blocks: [
      { type: 'p', text: 'The Regulatory Reporting page (Oil & Gas → Incident Management → Regulatory Reporting) compiles all SHA-256 sealed closure records into a structured evidence pack for regulatory submission.' },
      { type: 'h3', text: 'What the evidence pack contains' },
      { type: 'p', text: 'Expanding any incident row shows the full chain of custody across all 8 steps:' },
      { type: 'ul', items: [
        'AI OGI findings — severity, plume rate (kg/hr), thermal anomaly (°C), and the full AI diagnosis text',
        'Safety Officer decision — Step 7 decision type and note',
        'Ground response — response type, team lead, ERP work order reference',
        'Closure record — outcome, product loss (bbl), repair method, regulatory notification status, notes',
        'SHA-256 integrity seal — full 64-character hash with timestamp',
      ]},
      { type: 'h3', text: 'Filters' },
      { type: 'table',
        headers: ['Filter', 'Options'],
        rows: [
          ['Corridor',     'Filter to a single pipeline corridor'],
          ['Outcome',      'Repaired / Shut-in / False Alarm / Monitoring Only'],
          ['Regulatory',   'Show only incidents where the regulator was notified, or only those where it was not'],
        ],
      },
      { type: 'h3', text: 'Export and print' },
      { type: 'p', text: 'Click "Export CSV" to download the filtered record set as a spreadsheet including the full SHA-256 hash column. Click "Print" to generate a print-ready view for physical submission.' },
      { type: 'note', text: 'For DPR/NUPRC submissions (Nigeria), include the SHA-256 hash alongside the SOPF Form 7 or Form 8. For EPA (USA), reference it in the SPCC documentation. The hash can be independently verified against the closure record data.' },
    ],
  },

  // ── Mission Control ──────────────────────────────────────────────────────────
  {
    id: 'missions-list',
    section: 'Mission Control',
    title: 'Managing missions',
    icon: 'MapPin',
    keywords: ['missions', 'create', 'status', 'planned', 'completed', 'in progress', 'search', 'filter', 'detail'],
    blocks: [
      { type: 'p', text: 'Mission Control (sidebar → Mission Control) lists all drone survey and remediation missions across all verticals. Drone technicians use this as their dispatch view.' },
      { type: 'h3', text: 'List and search' },
      { type: 'p', text: 'Search by mission name. Filter by status: planned, in_progress, completed, processing. Each card shows mission name, location, flight date, image count, crop type, and a status badge.' },
      { type: 'h3', text: 'Creating a mission' },
      { type: 'p', text: 'Click "New Mission". Fill in the required fields (Name, Location, Flight Date) and optional fields (Crop Type, Area in ha, Description). Click "Create Mission" — you are taken directly to the mission detail page.' },
      { type: 'h3', text: 'Mission detail' },
      { type: 'ul', items: [
        'View metadata: crop type, area, image count.',
        'Upload images: supports single or multi-file selection.',
        'Image gallery: thumbnails with download and delete per image.',
        '"View Analysis" button — navigates to AI analysis for this mission.',
        '"Delete Mission" — with confirmation dialog.',
      ]},
    ],
  },
  {
    id: 'missions-upload',
    section: 'Mission Control',
    title: 'Large file upload (>10 GB)',
    icon: 'MapPin',
    keywords: ['upload', 'large file', 'chunked', 'telemetry', 'resume', 'progress'],
    blocks: [
      { type: 'p', text: 'For mission telemetry files larger than 10 GB, use the Large File Upload page (Mission Control → Upload Large File). This uses a resumable chunked upload that survives network interruptions.' },
      { type: 'ul', items: [
        'Drag and drop a file or use the file picker.',
        'A progress bar shows chunks uploaded and estimated time remaining.',
        'If the upload is interrupted (browser closed, network drop), re-open the page and select the same file — it will resume from where it left off.',
        'On completion the file is automatically ingested into the mission pipeline.',
      ]},
    ],
  },

  // ── Satellite Monitoring ─────────────────────────────────────────────────────
  {
    id: 'satellite-boundaries',
    section: 'Satellite Monitoring',
    title: 'Field Boundaries Map',
    icon: 'Satellite',
    keywords: ['satellite', 'boundaries', 'map', 'polygon', 'geojson', 'import', 'raw'],
    blocks: [
      { type: 'p', text: 'The Field Boundaries Map (Satellite Monitoring → Boundaries) is the underlying spatial data layer. The AgroTech Fields page is built on top of this data, but you can also manage boundaries directly here for non-AgroTech workflows.' },
      { type: 'p', text: 'Create, edit, and delete boundary polygons. Import from GeoJSON. All boundaries created in the AgroTech Fields page also appear here.' },
    ],
  },
  {
    id: 'satellite-imagery',
    section: 'Satellite Monitoring',
    title: 'Imagery records',
    icon: 'Satellite',
    keywords: ['imagery', 'satellite', 'acquisition', 'cloud cover', 'ndvi', 'ndwi', 'evi', 'savi', 'trend'],
    blocks: [
      { type: 'p', text: 'The Imagery page (Satellite Monitoring → Imagery) shows raw satellite imagery records per field. Filter by field and date range to see acquisition date, satellite name, cloud cover %, and vegetation index values for each scene.' },
      { type: 'p', text: 'A historical trend chart shows how index values have changed over time. This is the same data that powers the Temporal Analysis page in the AgroTech module.' },
    ],
  },
  {
    id: 'satellite-anomalies',
    section: 'Satellite Monitoring',
    title: 'Anomaly Dashboard (raw view)',
    icon: 'Satellite',
    keywords: ['anomaly', 'dashboard', 'satellite', 'raw', 'severity', 'status'],
    blocks: [
      { type: 'p', text: 'The Anomaly Dashboard (Satellite Monitoring → Anomalies) shows the full unfiltered anomaly feed across all fields and verticals. It is the same underlying data as the AgroTech Anomaly Alerts page, but without the Step 4 decision gate UI.' },
      { type: 'p', text: 'Use this page for raw data inspection, reporting, or cross-vertical analysis. Status management (mark as reviewed, add notes) is available here.' },
    ],
  },

  // ── Settings ─────────────────────────────────────────────────────────────────
  {
    id: 'settings-org',
    section: 'Settings',
    title: 'Organisation settings',
    icon: 'Settings',
    keywords: ['organisation', 'settings', 'name', 'logo', 'invite', 'team', 'members', 'roles', 'permissions', 'user management'],
    blocks: [
      { type: 'p', text: 'Organisation settings are available to Org Admins under the Settings section in the sidebar.' },
      { type: 'h3', text: 'General' },
      { type: 'p', text: 'Edit your organisation name, logo, and contact details.' },
      { type: 'h3', text: 'User Management' },
      { type: 'p', text: 'Invite users by email address. Assign one of the platform roles:' },
      { type: 'table',
        headers: ['Role', 'Typical user', 'Access level'],
        rows: [
          ['org_admin',                'Organisation Administrator', 'Full access — settings, user management, AI configuration, all verticals'],
          ['operations_field_manager', 'Field / Operations Manager', 'Mission management, anomaly review, AI analysis, analytics'],
          ['drone_technician',         'Drone Pilot / Technician',  'Mission creation and execution, flight data upload, mission view'],
          ['data_analyst',             'Data Analyst',              'Read access to all data; analytics and reports; no mission or anomaly write actions'],
        ],
      },
      { type: 'h3', text: 'Membership Requests' },
      { type: 'p', text: 'When a user registers with your organisation name, they appear here as a pending request. Accept or reject them from this panel.' },
    ],
  },
  {
    id: 'settings-ai',
    section: 'Settings',
    title: 'AI Configuration',
    icon: 'Settings',
    keywords: ['ai', 'gemini', 'openai', 'anthropic', 'azure', 'groq', 'openrouter', 'cloudflare', 'api key', 'model', 'configuration', 'provider', 'budget', 'usage', 'free'],
    blocks: [
      { type: 'p', text: 'Go to Settings → AI Configuration to set the AI provider and model for your organisation. The provider you configure here is used for all AI features: Ask Nabhos NLQ, drone flight briefings, weather checks, and the in-app Help Chat.' },
      { type: 'h3', text: 'Supported providers' },
      { type: 'table',
        headers: ['Provider', 'Active', 'Free tier', 'Key models'],
        rows: [
          ['Google Gemini',         '✅ Active (default)', 'No',  'gemini-1.5-pro, gemini-1.5-flash'],
          ['Cloudflare Workers AI', '✅ Active',           'Yes', 'llama-3.1-8b-instruct, mistral-7b'],
          ['Groq',                  '✅ Active',           'Yes', 'llama-3.3-70b-versatile, mixtral-8x7b'],
          ['OpenRouter',            '✅ Active',           'Yes', 'Access to 200+ models including free tiers'],
          ['OpenAI',                '⏸ Staged',            'No',  'gpt-4o, gpt-4-turbo, gpt-3.5-turbo'],
          ['Anthropic',             '⏸ Staged',            'No',  'claude-3-opus, claude-3-sonnet, claude-3-haiku'],
          ['Azure OpenAI',          '⏸ Staged',            'No',  'Configured per your Azure deployment'],
        ],
      },
      { type: 'h3', text: 'Configuring a provider' },
      { type: 'ul', items: [
        'Click the provider card you want to use.',
        'Select vision, text, and embedding models from the dropdowns (pre-filled with recommended defaults).',
        'Paste your API key. The key is stored encrypted and never shown again after saving. You can replace it at any time.',
        'Set an optional Monthly Budget (USD) — the system will warn when spend approaches this limit.',
        'Click "Save Configuration". Use "Test AI Connection" to verify the key and model are working before relying on the feature.',
      ]},
      { type: 'h3', text: 'Usage stats' },
      { type: 'p', text: 'The Active Configuration panel shows total requests, total tokens used, average confidence, and current-month spend. The Usage Breakdown table breaks these down by request type (NLQ, mission briefing, weather check) and by model.' },
      { type: 'note', text: 'Groq, OpenRouter, and Cloudflare Workers AI offer free tiers suitable for development or low-volume production use. They are marked "Free" in the provider selection panel.' },
    ],
  },
  {
    id: 'settings-assets',
    section: 'Settings',
    title: 'Approved Drone Assets',
    icon: 'Settings',
    keywords: ['assets', 'drone', 'approved', 'manufacturer', 'model', 'payload', 'sensor'],
    blocks: [
      { type: 'p', text: 'Settings → Approved Assets defines which drone models and sensor payloads are authorised for use in your organisation. These appear in the Survey Mission Planner (Step 5) asset selector.' },
      { type: 'p', text: 'Each asset entry includes manufacturer, model name, payload specs (camera resolution, sensor type, max flight time), and certification status.' },
    ],
  },
  {
    id: 'settings-audit',
    section: 'Settings',
    title: 'Audit Trail & Integrity',
    icon: 'Settings',
    keywords: ['audit', 'trail', 'tamper', 'integrity', 'log', 'immutable', 'chain'],
    blocks: [
      { type: 'p', text: 'Every human decision in the platform (Step 4 anomaly verification, Step 7 AI diagnosis review, mission approvals) is recorded in an immutable audit chain.' },
      { type: 'p', text: 'View the full audit log at Settings → Audit Trail. Each entry shows: timestamp, user, action type, target record, and any notes. Entries cannot be edited or deleted.' },
      { type: 'p', text: 'The Integrity Dashboard (Settings → Integrity) shows tamper-detection status. If a data record has been modified outside the application, a tamper alert is raised.' },
    ],
  },
  {
    id: 'settings-hierarchy',
    section: 'Settings',
    title: 'Organisational Hierarchy',
    icon: 'Settings',
    keywords: ['hierarchy', 'organisation', 'division', 'estate', 'garden', 'plot', 'node', 'tree', 'structure', 'create node', 'move node'],
    blocks: [
      { type: 'p', text: 'Go to Settings → Hierarchy to define the organisational tree that fields, corridors, and asset zones are assigned to. The hierarchy typically follows a pattern like Division → Estate → Garden/Plot, but any multi-level structure is supported.' },
      { type: 'h3', text: 'Creating a node' },
      { type: 'ul', items: [
        'Click "Add Node" to create a root-level node, or select an existing node and click "Add Child".',
        'Enter a name and optional JSON configuration for the node.',
        'Click "Create" — the node appears immediately in the tree.',
      ]},
      { type: 'h3', text: 'Editing and moving nodes' },
      { type: 'p', text: 'Click the edit icon on any node to rename it, change its configuration, or re-parent it to a different node. All fields and boundaries assigned to a node update automatically when the node is moved.' },
      { type: 'note', text: 'The hierarchy you define here appears as a searchable tree selector when creating or editing fields (AgroTech → Fields) and corridor boundaries (Oil & Gas → Corridors).' },
    ],
  },
  {
    id: 'settings-roles',
    section: 'Settings',
    title: 'Role & Screen Access',
    icon: 'Settings',
    keywords: ['roles', 'permissions', 'access', 'screen', 'rbac', 'restrict', 'module', 'role access', 'feature'],
    blocks: [
      { type: 'p', text: 'Go to Settings → Role Access to control which screens and features each role can access within your organisation. Permissions are evaluated in real time — changes take effect within 60 seconds for active sessions.' },
      { type: 'h3', text: 'How it works' },
      { type: 'p', text: 'Each row in the table represents a role + screen combination. Toggle the switch to grant or revoke access. The four baseline roles are:' },
      { type: 'table',
        headers: ['Role', 'Default access'],
        rows: [
          ['org_admin', 'Full access to all screens and settings'],
          ['operations_field_manager', 'All mission, anomaly, and analysis screens; no platform admin'],
          ['drone_technician', 'Mission creation and execution screens; no anomaly review or settings'],
          ['data_analyst', 'Read-only access to all data screens; no write actions'],
        ],
      },
      { type: 'note', text: 'Custom roles can be created by a Platform Admin via Platform Administration → Roles. Once created, they appear in this screen for per-org access configuration.' },
    ],
  },
  {
    id: 'settings-notifications',
    section: 'Settings',
    title: 'Notifications & Webhooks',
    icon: 'Settings',
    keywords: ['notifications', 'webhooks', 'alerts', 'email', 'push', 'webhook', 'event', 'subscribe', 'test'],
    blocks: [
      { type: 'p', text: 'Settings → Notifications controls how you receive alerts. Settings → Webhooks lets you push platform events to external systems (Slack, PagerDuty, custom endpoints).' },
      { type: 'h3', text: 'Notification preferences' },
      { type: 'p', text: 'Open the Notification Preferences panel to choose which event types trigger email or in-app notifications for your account. Options include: new anomaly detected, anomaly status changed, mission completed, analysis complete, data integrity alert.' },
      { type: 'h3', text: 'Webhooks' },
      { type: 'ul', items: [
        'Click "Add Webhook" and enter the endpoint URL.',
        'Select the event types to subscribe to.',
        'Use "Test Webhook" to send a sample payload and verify your endpoint is receiving correctly.',
        'Active webhooks are shown with last-delivery status and response code.',
      ]},
      { type: 'note', text: 'Webhook payloads are JSON and include the event type, timestamp, and the affected record ID. Delivery failures are retried up to 3 times with exponential back-off.' },
    ],
  },
  {
    id: 'settings-units',
    section: 'Settings',
    title: 'Units of Measure',
    icon: 'Settings',
    keywords: ['units', 'measure', 'hectares', 'acres', 'bigha', 'metric', 'imperial', 'area', 'distance', 'uom'],
    blocks: [
      { type: 'p', text: 'Go to Settings → Units of Measure to configure the area and distance units shown throughout the platform for your organisation.' },
      { type: 'table',
        headers: ['Setting', 'Options'],
        rows: [
          ['Area unit', 'Hectares (ha), Acres, Bighas'],
          ['Distance unit', 'Kilometres (km), Miles'],
        ],
      },
      { type: 'p', text: 'Changes apply immediately to all area displays across fields, corridors, zones, anomaly cards, and reports. Underlying database values are always stored in hectares; the display conversion is applied client-side.' },
    ],
  },

  // ── Infrastructure Intelligence ───────────────────────────────────────────────
  {
    id: 'infra-overview',
    section: 'Infrastructure Intelligence',
    title: 'Infrastructure Intelligence overview',
    icon: 'Building',
    keywords: ['infrastructure', 'road', 'railway', 'bridge', 'power line', 'construction', 'encroachment', 'earthwork', 'lidar', 'ndbi', 'mvp4'],
    blocks: [
      { type: 'p', text: 'The Infrastructure Intelligence module (enabled via Platform Admin → Module Marketplace) delivers the full satellite-to-drone-to-compliance intelligence loop for linear asset corridors and construction sites.' },
      { type: 'h3', text: 'Eight-step workflow' },
      { type: 'table',
        headers: ['Step', 'Page', 'Who acts'],
        rows: [
          ['1 — Asset Boundaries',       'Infrastructure → Asset Monitoring → Asset Boundaries',       'Infrastructure Engineer'],
          ['2 — Temporal Analysis',      'Infrastructure → Asset Monitoring → Temporal Analysis',      'Infrastructure Engineer'],
          ['3–4 — Anomaly Alerts',       'Infrastructure → Asset Monitoring → Anomaly Alerts',         'Infrastructure Engineer'],
          ['5 — Survey Planner',         'Infrastructure → Drone Operations → Survey Mission Planner', 'Drone Technician'],
          ['6 — Flight Data & Analysis', 'Infrastructure → Drone Operations → Flight Data & Analysis', 'Drone Technician'],
          ['7 — Analysis Review',        'Infrastructure → Drone Operations → Analysis Review',        'Infrastructure Engineer'],
          ['8 — Response Planning',      'Infrastructure → Compliance & Enforcement → Response Planning', 'Compliance Officer'],
          ['— — Construction Progress',  'Infrastructure → Compliance & Enforcement → Construction Progress', 'Project Manager'],
          ['— — Encroachment Detection', 'Infrastructure → Compliance & Enforcement → Encroachment Detection', 'Compliance Officer'],
        ],
      },
      { type: 'h3', text: 'Additional pages' },
      { type: 'table',
        headers: ['Page', 'Location', 'Purpose'],
        rows: [
          ['Operations Dashboard', 'Infrastructure → Operations Dashboard', 'Cross-step KPI summary and action queue for all 8 steps'],
        ],
      },
      { type: 'note', text: 'The module must be enabled for your organisation in the Module Marketplace before the Infrastructure section appears.' },
    ],
  },
  {
    id: 'infra-boundaries',
    section: 'Infrastructure Intelligence',
    title: 'Step 1 — Asset Boundaries',
    icon: 'Building',
    keywords: ['asset', 'boundary', 'corridor', 'construction site', 'power line', 'road', 'railway', 'bridge', 'slope zone', 'project id', 'owner', 'monitoring frequency'],
    blocks: [
      { type: 'p', text: 'The Asset Boundaries page registers the spatial footprints of your infrastructure assets. Each boundary is the region-of-interest sent to the Google Earth Engine pipeline for Sentinel-2 satellite queries (Step 2).' },
      { type: 'h3', text: 'Creating an asset boundary' },
      { type: 'p', text: 'Click "Add Asset" and complete the form:' },
      { type: 'table',
        headers: ['Field', 'Required', 'Notes'],
        rows: [
          ['Asset Name',      'Yes', 'Descriptive name, e.g. "Lagos–Ibadan Expressway Corridor"'],
          ['Asset Type',      'No',  'Road/highway, railway, power-line RoW, construction site, slope zone, bridge zone, pipeline RoW, water body'],
          ['Project / Line ID','Yes','Your asset register ID, e.g. "RD-LIE-001"'],
          ['Owner / Contractor','No','Company or authority responsible, e.g. "FERMA, Julius Berger"'],
          ['Monitoring Frequency','No','Daily, Weekly, Monthly, or On Demand'],
        ],
      },
      { type: 'p', text: 'After completing the form, draw the boundary polygon on the embedded map (supports polygon and polyline tools). Click "Save Asset" to register.' },
      { type: 'h3', text: 'Map view' },
      { type: 'p', text: 'Switch to Map view to see all registered boundaries rendered as blue polygons. Click any polygon to open an info popup with Edit and Delete actions.' },
    ],
  },
  {
    id: 'infra-temporal',
    section: 'Infrastructure Intelligence',
    title: 'Step 2 — Temporal Analysis',
    icon: 'Building',
    keywords: ['temporal', 'ndbi', 'ndvi', 'ndwi', 'lst', 'satellite', 'imagery', 'earthwork', 'timeline', 'sentinel'],
    blocks: [
      { type: 'p', text: 'Temporal Analysis fetches multi-date Sentinel-2 imagery for a selected asset boundary and displays four satellite indices over time.' },
      { type: 'h3', text: 'Indices explained' },
      { type: 'table',
        headers: ['Index', 'What it measures', 'Relevance for infrastructure'],
        rows: [
          ['NDBI', 'Normalised Difference Built-Up Index', 'Rising NDBI indicates new bare soil, concrete, or construction activity in the corridor'],
          ['NDVI', 'Normalised Difference Vegetation Index', 'Dropping NDVI signals land clearance or vegetation removal alongside the RoW'],
          ['NDWI', 'Water / Moisture Index', 'Useful for drainage change detection and identifying waterlogging from earthworks'],
          ['LST',  'Land Surface Temperature', 'Thermal anomalies from exposed dark surfaces (asphalt, compacted earth)'],
        ],
      },
      { type: 'p', text: 'Select an asset boundary and a date range, then click "Fetch Imagery" to queue a Google Earth Engine acquisition. Results appear within 2–3 minutes.' },
      { type: 'note', text: 'NDBI is the primary indicator for infrastructure change detection. An increase of more than +0.05 over the baseline is typically flagged as a potential unauthorised construction event.' },
    ],
  },
  {
    id: 'infra-anomalies',
    section: 'Infrastructure Intelligence',
    title: 'Steps 3 & 4 — Anomaly Alerts',
    icon: 'Building',
    keywords: ['anomaly', 'alert', 'ndbi', 'earthwork', 'encroachment', 'unauthorised', 'construction', 'stop-work', 'verify', 'step 3', 'step 4', 'human verification'],
    blocks: [
      { type: 'p', text: 'Step 3 is the AI analysis stage: satellite change detection generates probable-cause narratives for each detected anomaly (unauthorised construction, earthwork change, slope instability, vegetation die-off). Step 4 is your human verification gate.' },
      { type: 'h3', text: 'Step 4 — Your decision options' },
      { type: 'table',
        headers: ['Action', 'Effect', 'Next step'],
        rows: [
          ['Confirm — Schedule RGB/LiDAR Drone Survey', 'Status → investigating', 'Anomaly appears in Survey Mission Planner (Step 5)'],
          ['Confirm — Ground Team Inspection Only',     'Status → investigating', 'Ground team dispatched, no drone required'],
          ['Issue Stop-Work Notice',                    'Status → investigating', 'Immediate enforcement notice issued; tracked in Response Planning'],
          ['Reject — False Positive',                  'Status → false_positive','Dismissed from queue; recorded in audit trail'],
          ['Defer — Monitor Next Pass',                'Status → pending',       'Remains in queue for review after next satellite overpass'],
        ],
      },
      { type: 'h3', text: 'Running monitoring manually' },
      { type: 'p', text: 'Click "Run Monitoring" (top right) to trigger an immediate satellite change-detection scan across all registered infrastructure boundaries. Select a specific asset from the dropdown to scan only that corridor.' },
    ],
  },
  {
    id: 'infra-survey',
    section: 'Infrastructure Intelligence',
    title: 'Step 5 — Survey Mission Planner',
    icon: 'Building',
    keywords: ['survey', 'planner', 'rgb', 'lidar', 'photogrammetry', 'gsd', 'overlap', 'gcp', 'mission', 'drone'],
    blocks: [
      { type: 'p', text: 'The Survey Mission Planner generates an AI-derived flight plan for an RGB + LiDAR drone survey over a confirmed anomaly. Only anomalies with status "investigating" (confirmed in Step 4) appear in the selection list.' },
      { type: 'h3', text: 'AI-derived plan parameters' },
      { type: 'table',
        headers: ['Parameter', 'Typical value', 'Notes'],
        rows: [
          ['Primary Payload', 'DJI Zenmuse P1 (45 MP) or Sony A7R IV', 'Structural anomalies use higher-resolution payload'],
          ['LiDAR',           'Velodyne VLP-16 (volume jobs only)',      'Fitted when earthwork volumes are required'],
          ['Survey Altitude', '50–80 m AGL',                            'Lower for bridge/structural inspections'],
          ['Target GSD',      '1.5–2.5 cm/pixel',                       'Drives overlap and altitude recommendations'],
          ['Front / Side Overlap', '85% / 80%',                         'Required for survey-grade photogrammetric accuracy'],
        ],
      },
      { type: 'p', text: 'Give the mission a name and add pilot notes (GCP placement, access route), then click "Create Mission". The mission is registered in Mission Control and ready for data upload.' },
    ],
  },
  {
    id: 'infra-flight',
    section: 'Infrastructure Intelligence',
    title: 'Step 6 — Flight Data & Analysis',
    icon: 'Building',
    keywords: ['upload', 'lidar', 'rgb', 'geotiff', 'las', 'laz', 'point cloud', 'dem', 'photogrammetry', 'analysis', 'earthwork', 'volume'],
    blocks: [
      { type: 'p', text: 'After a drone survey, upload the collected data via Mission Control → Upload Data. Flight Data & Analysis lists all infrastructure survey missions and displays their AI volumetric analysis results.' },
      { type: 'h3', text: 'Accepted file types' },
      { type: 'table',
        headers: ['File type', 'Format', 'Required?'],
        rows: [
          ['RGB Orthomosaic / Images', 'GeoTIFF / JPEG2000 / folder of JPEG', 'Required'],
          ['LiDAR Point Cloud',        'LAS / LAZ (v1.2–1.4)',               'Optional — mandatory for legal-grade volumes'],
          ['Digital Elevation Model',  'GeoTIFF (32-bit float)',              'Optional — alternative to raw point cloud'],
          ['Flight Log / Telemetry',   'CSV / KML / GPX',                    'Required'],
          ['GCP Report / Pre-flight',  'PDF',                                'Optional — required for certified surveys'],
        ],
      },
      { type: 'h3', text: 'AI analysis output' },
      { type: 'p', text: 'Once analysis is complete the expanded mission row shows: cut volume (m³), fill volume (m³), net earthwork delta (m³), and the AI diagnosis narrative. Click "Re-analyse" to re-run the AI engine after uploading additional data.' },
    ],
  },
  {
    id: 'infra-review',
    section: 'Infrastructure Intelligence',
    title: 'Step 7 — Analysis Review',
    icon: 'Building',
    keywords: ['review', 'hitl', 'human', 'engineer', 'verify', 'stop-work', 'compliance', 'earthwork', 'confirm', 'decision'],
    blocks: [
      { type: 'p', text: 'Analysis Review is the second human-in-the-loop gate. Infrastructure Engineers review the AI photogrammetric findings and decide on the appropriate compliance action.' },
      { type: 'h3', text: 'Step 7 decision options' },
      { type: 'table',
        headers: ['Decision', 'Effect'],
        rows: [
          ['Confirm Critical — Immediate Stop-Work Required',       'Moves to Step 8 Response Planning as critical priority'],
          ['Confirm — Issue Compliance Notice (Non-critical)',       'Moves to Step 8 as compliance notice'],
          ['Confirm — Elevated Monitoring (No Enforcement Yet)',     'Returns to monitoring queue with raised priority'],
          ['Override — Downgrade to Low Priority',                  'Recorded in audit trail; no enforcement action'],
          ['Reject — False Positive',                               'Dismissed; recorded in audit trail'],
        ],
      },
      { type: 'p', text: 'Only missions with AI analysis status "complete" and no prior Step 7 decision appear in this queue. Add an engineer note before confirming — it becomes part of the immutable audit record.' },
    ],
  },
  {
    id: 'infra-response',
    section: 'Infrastructure Intelligence',
    title: 'Step 8a — Response Planning',
    icon: 'Building',
    keywords: ['response', 'stop-work', 'compliance notice', 'enforcement', 'legal referral', 'officer', 'dispatch'],
    blocks: [
      { type: 'p', text: 'Response Planning shows missions confirmed in Step 7 (confirmed_critical, confirmed_compliance, or confirmed_monitor status) that have not yet had a response dispatched.' },
      { type: 'h3', text: 'Response types' },
      { type: 'ul', items: [
        'Stop-Work Order — formal order to cease all construction activity immediately',
        'Compliance Notice — written notice requiring corrective action within a deadline',
        'Engineer Site Inspection — physical inspection order',
        'Emergency Reinstatement — urgent reinstatement of disturbed infrastructure',
        'Refer to Legal / Regulator — escalation to legal counsel or regulatory body',
      ]},
      { type: 'p', text: 'Each response requires a Responsible Officer name and Response Deadline (hours). Optionally add a Reference / Notice Number for cross-referencing with your document management system.' },
    ],
  },
  {
    id: 'infra-construction',
    section: 'Infrastructure Intelligence',
    title: 'Step 8b — Construction Progress',
    icon: 'Building',
    keywords: ['construction', 'progress', 'cut', 'fill', 'volume', 'earthwork', 'completion', 'schedule', 'volumetric'],
    blocks: [
      { type: 'p', text: 'Construction Progress aggregates earthwork volumetric data from all completed infrastructure drone surveys. It provides a portfolio-level view of cut/fill volumes and per-site completion tracking.' },
      { type: 'h3', text: 'Volumetric summary cards' },
      { type: 'ul', items: [
        'Total Cut Volume (m³) — cumulative earth excavated across all surveyed sites',
        'Total Fill Volume (m³) — cumulative earth deposited / compacted',
        'Net Earthwork Δ (m³) — cut minus fill; positive means more excavation than fill (may indicate disposal issue)',
      ]},
      { type: 'h3', text: 'Completion bar' },
      { type: 'p', text: 'Each mission row shows a colour-coded completion bar when completion_percent metadata is available from the AI analysis. Red (<25%), amber (<60%), blue (<90%), green (≥90%).' },
    ],
  },
  {
    id: 'infra-encroachment',
    section: 'Infrastructure Intelligence',
    title: 'Step 8c — Encroachment Detection',
    icon: 'Building',
    keywords: ['encroachment', 'unauthorised', 'ndbi', 'row', 'right-of-way', 'construction', 'illegal', 'notice', 'legal referral', 'scan'],
    blocks: [
      { type: 'p', text: 'Encroachment Detection filters the anomaly queue to show only events with NDBI-driven change types (encroachment, unauthorised construction, built-up index change). It provides a focused compliance enforcement view.' },
      { type: 'h3', text: 'Running an NDBI scan' },
      { type: 'p', text: 'Click "Scan RoW (NDBI)" to trigger an immediate change-detection scan across all infrastructure boundaries. The scan compares the latest Sentinel-2 acquisition against the 90-day NDBI baseline.' },
      { type: 'h3', text: 'Escalation options' },
      { type: 'table',
        headers: ['Action', 'Effect'],
        rows: [
          ['Issue Encroachment Notice',      'Records action; creates legal notice reference in audit trail'],
          ['Refer to Legal / Regulator',     'Escalates to legal/regulatory body; records referral date'],
          ['Monitor — No Immediate Action',  'Keeps in monitoring queue; no enforcement action yet'],
          ['Reject — False Positive',        'Dismisses from queue; permanently recorded as false positive'],
        ],
      },
      { type: 'note', text: 'All escalation decisions are recorded in the immutable audit trail. Encroachment notices can be cross-referenced with the Response Planning page (Step 8a) for full enforcement tracking.' },
    ],
  },

  // ── Infrastructure — Operations Dashboard ─────────────────────────────────
  {
    id: 'infra-dashboard',
    section: 'Infrastructure Intelligence',
    title: 'Operations Dashboard',
    icon: 'Building',
    keywords: ['dashboard', 'kpi', 'overview', 'operations', 'infrastructure', 'summary', 'action queue', 'situational awareness'],
    blocks: [
      { type: 'p', text: 'The Infrastructure Dashboard (Infrastructure → Operations Dashboard) gives a live situational overview of the full 8-step intelligence loop in a single view — no need to visit each page individually to check for pending actions.' },
      { type: 'h3', text: 'KPI cards' },
      { type: 'table',
        headers: ['Card', 'What it shows', 'Links to'],
        rows: [
          ['Assets Monitored',  'Total active infrastructure asset boundary polygons', 'Asset Boundaries'],
          ['Pending Anomalies', 'Anomalies awaiting Step 4 human verification',         'Anomaly Alerts'],
          ['Active Surveys',    'Drone survey missions currently in-progress or pending upload', 'Flight Data & Analysis'],
          ['Response Required', 'Confirmed incidents awaiting response team dispatch',  'Response Planning'],
        ],
      },
      { type: 'h3', text: 'Action Queue' },
      { type: 'p', text: 'The workflow step list shows each of the 8 steps with a count badge where human action is currently needed. Red badges indicate urgent items:' },
      { type: 'ul', items: [
        'Steps 3–4 Anomaly Alerts — anomalies awaiting your Step 4 decision',
        'Step 7 Analysis Review — drone missions with complete AI diagnosis waiting for sign-off',
        'Step 8 Response Planning — confirmed incidents awaiting compliance officer action',
      ]},
      { type: 'p', text: 'Quick links at the bottom right give direct access to Construction Progress, Encroachment Detection, and Response Planning. The amber attention banner at the top shows the total action count across all three gates.' },
    ],
  },

  // ── Linear Assets Intelligence ────────────────────────────────────────────────
  {
    id: 'linear-dashboard',
    section: 'Linear Assets Intelligence',
    title: 'Linear Assets Operations Dashboard',
    icon: 'Zap',
    keywords: ['linear', 'assets', 'power line', 'railway', 'corridor', 'encroachment', 'track obstruction', 'dashboard', 'buffer'],
    blocks: [
      { type: 'p', text: 'The Linear Assets Corridor Intelligence Dashboard (/linear-assets/dashboard) provides real-time situational awareness across power line grid feeders, high-voltage transmission lines, and railway right-of-way networks.' },
      { type: 'h3', text: 'Key metric cards' },
      { type: 'ul', items: [
        'Monitored Corridor Lengths — total linear distance and lateral buffer zones monitored via orbital satellite.',
        'Track Obstruction Alerts — near-real-time (<6h SLA) foreign object and track hazard detections.',
        'Encroachment Contact Risks — vegetation canopies projected to breach wire clearance buffers within 60 days.',
        '8-Step Intelligence Loop Navigation — direct deep links to every workflow stage from Corridor Boundary registration to Vegetation Clearance Work Orders.',
      ]},
    ],
  },
  {
    id: 'linear-encroachment',
    section: 'Linear Assets Intelligence',
    title: 'Vegetation Encroachment & Growth Modeling',
    icon: 'Zap',
    keywords: ['encroachment', 'vegetation', 'growth rate', 'projection', 'days to contact', 'power line', 'wire clearance', 'trimming'],
    blocks: [
      { type: 'p', text: 'Encroachment Projections (/linear-assets/encroachment) calculates monthly vegetation canopy growth rates (m/month) from temporal NDVI deltas across corridor segments.' },
      { type: 'h3', text: 'Days-to-Contact Forecasting' },
      { type: 'p', text: 'For each corridor kilometre marker, the system extrapolates the distance between the wire buffer and the tree canopy to calculate an exact projected contact date. Critical alerts trigger automatically when days-to-contact is under 60 days.' },
      { type: 'h3', text: 'Corridor Patrol Planning' },
      { type: 'p', text: 'Click "Plan Drone Patrol" on any encroaching segment to auto-generate a targeted drone survey mission (Step 5) with high-density waypoints directly over the hazard zone.' },
    ],
  },
  {
    id: 'linear-obstructions',
    section: 'Linear Assets Intelligence',
    title: 'Track Obstruction Rapid Triage (<6h SLA)',
    icon: 'Zap',
    keywords: ['railway', 'track', 'obstruction', 'debris', 'rockfall', 'buckling', 'sla', 'rapid triage'],
    blocks: [
      { type: 'p', text: 'Track Obstructions (/linear-assets/obstructions) displays real-time hazard detections identified along railway corridors within 6 hours of orbital satellite overpasses.' },
      { type: 'h3', text: 'Supported Hazard Classes' },
      { type: 'table',
        headers: ['Hazard Class', 'Detection Method', 'Operational Action'],
        rows: [
          ['Track Obstruction / Foreign Object', 'High-res spectral & spatial differencing', 'Immediate track maintenance dispatch'],
          ['Rockfall Debris', 'Slope anomaly & DEM differencing', 'Emergency rail safety clearance team'],
          ['Thermal Track Buckling', 'Surface temperature (LST) anomaly hotspot', 'Speed restriction & thermal track inspection'],
        ],
      },
      { type: 'h3', text: 'Triage Workflow' },
      { type: 'p', text: 'Safety officers can verify or clear obstruction alerts, view precise GPS coordinates with SLA latency timestamps, and dispatch emergency response work orders with attached satellite evidence packages.' },
    ],
  },
  {
    id: 'linear-workflow',
    section: 'Linear Assets Intelligence',
    title: 'Corridor Drone Patrols & Clearance Work Orders',
    icon: 'Zap',
    keywords: ['drone patrol', 'lidar', 'clearance', 'work orders', 'contractor', 'tree trimming', 'rescan'],
    blocks: [
      { type: 'p', text: 'The complete 8-step linear assets lifecycle provides end-to-end operational execution:' },
      { type: 'ul', items: [
        'Survey Planner (/linear-assets/survey-planner) — corridor grid path generation with multi-sensor payload selection.',
        'Flight Data & Diagnostics (/linear-assets/flight-analysis) — LiDAR point-cloud ingestion and wire-to-canopy proximity calculation.',
        'Analysis Review (/linear-assets/analysis-review) — Asset integrity engineer sign-off with SHA-256 digital seals.',
        'Clearance Planning & Work Orders (/linear-assets/response, /linear-assets/work-orders) — contractor work order dispatch for tree trimmers and track crews.',
        'Clearance Efficacy (/linear-assets/efficacy) — automated 14-day post-clearance rescan to verify that vegetation was cleared to required buffer depths.',
      ]},
    ],
  },

  // ── Environmental Geotech ─────────────────────────────────────────────────────
  {
    id: 'env-overview',
    section: 'Environmental Geotech',
    title: 'Environmental Geotech overview',
    icon: 'TreePine',
    keywords: ['environmental', 'geotech', 'deforestation', 'wetland', 'ramsar', 'carbon', 'geohazard', 'landslide', 'forest', 'protected zone', 'mvp5'],
    blocks: [
      { type: 'p', text: 'The Environmental Geotech module (enabled via Platform Admin → Module Marketplace) delivers the full satellite-to-drone-to-enforcement intelligence loop for protected zones, carbon credit parcels, and geohazard corridors.' },
      { type: 'h3', text: 'Eight-step workflow' },
      { type: 'table',
        headers: ['Step', 'Page', 'Who acts'],
        rows: [
          ['1 — Zone Boundaries',       'Environmental → Zone Monitoring → Zone Boundaries',       'Environmental Officer'],
          ['2 — Temporal Analysis',     'Environmental → Zone Monitoring → Temporal Analysis',     'Environmental Officer'],
          ['3–4 — Anomaly Alerts',      'Environmental → Zone Monitoring → Anomaly Alerts',        'Environmental Officer'],
          ['5 — Survey Planner',        'Environmental → Drone Operations → Survey Mission Planner','Drone Technician'],
          ['6 — Flight Data & Analysis','Environmental → Drone Operations → Flight Data & Analysis','Drone Technician'],
          ['7 — Analysis Review',       'Environmental → Drone Operations → Analysis Review',       'Environmental Officer'],
          ['8a — Enforcement Actions',  'Environmental → Enforcement & Reporting → Enforcement Actions','Compliance Officer'],
          ['8b — Carbon Verification',  'Environmental → Enforcement & Reporting → Carbon Credit Verification','Compliance Officer'],
          ['— — Geohazard Risk',        'Environmental → Geohazard Risk',                          'Geotechnical Specialist'],
        ],
      },
      { type: 'h3', text: 'Additional pages' },
      { type: 'table',
        headers: ['Page', 'Location', 'Purpose'],
        rows: [
          ['Operations Dashboard', 'Environmental → Operations Dashboard', 'Cross-step KPI summary and action queue for all 8 steps'],
        ],
      },
      { type: 'note', text: 'The module must be enabled for your organisation in the Module Marketplace (Platform Admin) before the Environmental section appears.' },
    ],
  },

  // ── Environmental — Operations Dashboard ───────────────────────────────────
  {
    id: 'env-dashboard',
    section: 'Environmental Geotech',
    title: 'Operations Dashboard',
    icon: 'TreePine',
    keywords: ['dashboard', 'kpi', 'overview', 'operations', 'environmental', 'summary', 'action queue', 'situational awareness'],
    blocks: [
      { type: 'p', text: 'The Environmental Dashboard (Environmental → Operations Dashboard) gives a live situational overview of the full 8-step intelligence loop in a single view — no need to visit each page individually to check for pending actions.' },
      { type: 'h3', text: 'KPI cards' },
      { type: 'table',
        headers: ['Card', 'What it shows', 'Links to'],
        rows: [
          ['Zones Monitored',   'Total active protected zone boundary polygons',             'Zone Boundaries'],
          ['Pending Anomalies', 'Anomalies awaiting Step 4 human verification',              'Anomaly Alerts'],
          ['Active Surveys',    'Drone survey missions currently in-progress or pending upload', 'Flight Data & Analysis'],
          ['Open Enforcements', 'Confirmed incidents with no enforcement outcome yet recorded', 'Enforcement Actions'],
        ],
      },
      { type: 'h3', text: 'Action Queue' },
      { type: 'p', text: 'The workflow step list shows each of the 8 steps with a count badge where human action is currently needed. Red badges indicate urgent items:' },
      { type: 'ul', items: [
        'Steps 3–4 Anomaly Alerts — anomalies awaiting your Step 4 decision',
        'Step 7 Analysis Review — drone missions with complete AI diagnosis waiting for Environmental Officer sign-off',
        'Step 8 Enforcement Actions — confirmed violations awaiting authority notification or legal referral',
      ]},
      { type: 'p', text: 'Quick links at the bottom right give direct access to Carbon Credit Verification, Geohazard Risk, and Enforcement Actions. The amber attention banner at the top shows the total action count across all three gates.' },
    ],
  },
  {
    id: 'env-boundaries',
    section: 'Environmental Geotech',
    title: 'Step 1 — Zone Boundaries',
    icon: 'TreePine',
    keywords: ['zone', 'boundary', 'ramsar', 'national park', 'forest reserve', 'carbon parcel', 'geohazard zone', 'buffer zone', 'mining exclusion', 'protection status', 'regulator'],
    blocks: [
      { type: 'p', text: 'Zone Boundaries registers the spatial footprints of your protected areas. Each boundary becomes the region-of-interest for Sentinel-2 satellite queries (Step 2) and provides the legal parcel geometry for enforcement documentation.' },
      { type: 'h3', text: 'Creating a zone boundary' },
      { type: 'p', text: 'Click "Add Zone" and complete the form:' },
      { type: 'table',
        headers: ['Field', 'Required', 'Notes'],
        rows: [
          ['Zone Name',           'Yes', 'Descriptive name, e.g. "Sundarbans East Reserve"'],
          ['Zone Type',           'No',  'Forest Reserve, National Park, Ramsar Wetland, Carbon Credit Parcel, Geohazard Zone, Buffer Zone, Mining Exclusion Zone'],
          ['Protection Status',   'No',  'Legally Protected, World Heritage, Ramsar (Wetland Convention), Carbon Credit Registered, Proposed'],
          ['Regulator / Authority','No', 'Name of the government body responsible, e.g. "Ministry of Environment"'],
          ['Monitoring Cadence',  'No',  'Daily, Weekly, Monthly, or Seasonal'],
        ],
      },
      { type: 'p', text: 'After completing the form, draw the zone polygon on the embedded map. Saved zones appear as colour-coded polygons in Map view (green = forest reserve, blue = wetland, purple = carbon parcel, red = geohazard zone).' },
    ],
  },
  {
    id: 'env-temporal',
    section: 'Environmental Geotech',
    title: 'Step 2 — Temporal Analysis',
    icon: 'TreePine',
    keywords: ['temporal', 'ndvi', 'ndwi', 'nbr', 'lst', 'canopy', 'wetland', 'burn', 'satellite', 'sentinel', 'imagery', 'timeline', 'deforestation'],
    blocks: [
      { type: 'p', text: 'Temporal Analysis fetches multi-date Sentinel-2 imagery for a selected protected zone and displays four environmental indices over time.' },
      { type: 'h3', text: 'Environmental indices explained' },
      { type: 'table',
        headers: ['Index', 'What it measures', 'Environmental relevance'],
        rows: [
          ['NDVI', 'Normalised Difference Vegetation Index', 'Declining values indicate deforestation, illegal clearing, or drought stress'],
          ['NDWI', 'Normalised Difference Water Index',      'Falling values signal wetland drainage or encroachment reducing moisture'],
          ['NBR',  'Normalised Burn Ratio',                  'Strongly negative values confirm wildfire burn scars and affected area'],
          ['LST',  'Land Surface Temperature',               'Thermal anomalies from canopy loss exposing dark soil to direct sun'],
        ],
      },
      { type: 'p', text: 'Select a protected zone and a date range, then click "Fetch Imagery" to queue a Sentinel-2 acquisition via Google Earth Engine. Results appear within 2–3 minutes.' },
      { type: 'note', text: 'NDVI is the primary deforestation indicator. A sustained decline of more than −0.05 from the 90-day seasonal baseline is typically flagged as a potential illegal clearing event.' },
    ],
  },
  {
    id: 'env-anomalies',
    section: 'Environmental Geotech',
    title: 'Steps 3 & 4 — Anomaly Alerts',
    icon: 'TreePine',
    keywords: ['anomaly', 'alert', 'deforestation', 'clearing', 'wetland', 'illegal', 'mining', 'enforcement', 'verify', 'step 3', 'step 4', 'human verification', 'ndvi', 'authority'],
    blocks: [
      { type: 'p', text: 'Step 3 is the AI analysis stage: satellite change detection generates probable-cause narratives for each detected anomaly (deforestation, illegal clearing, wetland encroachment, illegal mining, wildfire). Step 4 is the human verification gate.' },
      { type: 'h3', text: 'Step 4 — Your decision options' },
      { type: 'table',
        headers: ['Action', 'Effect', 'Next step'],
        rows: [
          ['Confirm — Schedule Canopy Drone Survey',      'Status → investigating', 'Anomaly appears in Survey Mission Planner (Step 5)'],
          ['Confirm — Notify Environmental Authority',    'Status → investigating', 'Enforcement action triggered; authority contacted directly'],
          ['Confirm — Ground Patrol Inspection Only',     'Status → investigating', 'Patrol dispatched; no drone required'],
          ['Reject — False Positive',                    'Status → false_positive','Dismissed from queue; recorded in audit trail'],
          ['Defer — Monitor Next Satellite Pass',        'Status → pending',       'Remains in queue for review after next overpass'],
        ],
      },
      { type: 'h3', text: 'Running monitoring manually' },
      { type: 'p', text: 'Click "Run Monitoring" to trigger an immediate satellite change-detection scan across all registered environmental zones. Select a specific zone from the dropdown to scan only that parcel.' },
    ],
  },
  {
    id: 'env-survey',
    section: 'Environmental Geotech',
    title: 'Step 5 — Survey Mission Planner',
    icon: 'TreePine',
    keywords: ['survey', 'planner', 'fixed-wing', 'rgb', 'multispectral', 'canopy', 'gsd', 'overlap', 'mission', 'drone', 'bvlos', 'notam'],
    blocks: [
      { type: 'p', text: 'The Survey Mission Planner generates an AI-derived flight plan for a fixed-wing RGB + multispectral canopy survey over a confirmed zone anomaly. Only anomalies with status "investigating" appear in the selection list.' },
      { type: 'h3', text: 'Why fixed-wing?' },
      { type: 'p', text: 'Environmental zones are typically large (hundreds to thousands of hectares). Fixed-wing platforms (e.g. Penguin C, WingtraOne) provide the wide-area coverage required in a single mission, with multispectral payloads for NDVI and biomass calculation.' },
      { type: 'h3', text: 'AI-derived plan parameters' },
      { type: 'table',
        headers: ['Parameter', 'Typical value', 'Notes'],
        rows: [
          ['Platform',            'Fixed-wing (Penguin C / WingtraOne)',         'Provides wide-area coverage for large forest and wetland zones'],
          ['Primary Payload',     'Sony RX1R II RGB (42.4 MP)',                  'High-resolution base imagery for canopy classification'],
          ['Multispectral',       'Micasense RedEdge-MX or Altum-PT',            'Required for NDVI, NDWI, and biomass calculation'],
          ['Survey Altitude',     '120–150 m AGL',                               'Higher for fire/smoke zones; lower for wetland detail'],
          ['Target GSD',          '3–4 cm/pixel',                                'Sufficient for per-tree-crown delineation at scale'],
          ['Max Wind Speed',      '12 m/s',                                      'Fixed-wing limit; higher tolerance than multirotor'],
        ],
      },
      { type: 'note', text: 'Fixed-wing surveys over national parks and Ramsar wetlands typically require BVLOS (Beyond Visual Line of Sight) authorisation and a filed NOTAM. Obtain clearance before launch.' },
    ],
  },
  {
    id: 'env-flight',
    section: 'Environmental Geotech',
    title: 'Step 6 — Flight Data & Analysis',
    icon: 'TreePine',
    keywords: ['upload', 'rgb', 'multispectral', 'geotiff', 'canopy', 'analysis', 'deforestation', 'biomass', 'wetland', 'burn', 'calibration panel'],
    blocks: [
      { type: 'p', text: 'After a canopy drone survey, upload the collected data via Mission Control → Upload Data. Flight Data & Analysis lists all environmental survey missions and displays their AI canopy analysis results.' },
      { type: 'h3', text: 'Accepted file types' },
      { type: 'table',
        headers: ['File type', 'Format', 'Required?'],
        rows: [
          ['RGB Orthomosaic / Images',        'GeoTIFF / JPEG2000 / folder of JPEG', 'Required'],
          ['Multispectral / Hyperspectral',   'GeoTIFF (multi-band)',                'Required — for NDVI, NDWI, biomass'],
          ['Flight Log / Telemetry',          'CSV / KML / GPX',                    'Required'],
          ['Radiometric Calibration Panels',  'JPEG / TIFF',                        'Optional — required for scientific validation'],
          ['Airspace Clearance / NOTAM',      'PDF',                                'Optional — required for regulated airspace'],
        ],
      },
      { type: 'h3', text: 'AI canopy analysis output' },
      { type: 'p', text: 'Once analysis is complete, the expanded mission row shows: canopy loss (ha), canopy loss (%), burn area (ha), wetland loss (ha), and the AI diagnosis narrative. Click "Re-analyse" to re-run after additional data upload.' },
    ],
  },
  {
    id: 'env-review',
    section: 'Environmental Geotech',
    title: 'Step 7 — Analysis Review',
    icon: 'TreePine',
    keywords: ['review', 'hitl', 'human', 'officer', 'verify', 'enforcement', 'canopy', 'confirm', 'decision', 'deforestation', 'authority'],
    blocks: [
      { type: 'p', text: 'Analysis Review is the second human-in-the-loop gate. Environmental Officers review the AI canopy findings and decide on the appropriate enforcement action.' },
      { type: 'h3', text: 'Step 7 decision options' },
      { type: 'table',
        headers: ['Decision', 'Effect'],
        rows: [
          ['Confirm Critical — Immediate Enforcement Required',   'Moves to Step 8 Enforcement Actions as critical priority'],
          ['Confirm — Issue Formal Enforcement Notice',          'Moves to Step 8 as enforcement notice priority'],
          ['Confirm — Elevated Monitoring (No Enforcement Yet)', 'Returns to monitoring queue with raised priority'],
          ['Override — Downgrade to Low Priority',              'Recorded in audit trail; no enforcement action'],
          ['Reject — False Positive',                           'Dismissed; recorded in audit trail'],
        ],
      },
      { type: 'p', text: 'Only missions with AI analysis status "complete" and no prior Step 7 decision appear in this queue. Add an officer note before confirming — it becomes part of the immutable audit record.' },
    ],
  },
  {
    id: 'env-enforcement',
    section: 'Environmental Geotech',
    title: 'Step 8a — Enforcement Actions',
    icon: 'TreePine',
    keywords: ['enforcement', 'action', 'cease', 'desist', 'authority', 'legal', 'referral', 'remediation', 'fine', 'patrol', 'officer', 'dispatch'],
    blocks: [
      { type: 'p', text: 'Enforcement Actions shows missions confirmed in Step 7 (confirmed_critical or confirmed_enforcement status) that have not yet had an action dispatched.' },
      { type: 'h3', text: 'Action types' },
      { type: 'ul', items: [
        'Notify Environmental Authority — formal notification to the regulator with evidence package',
        'Issue Cease-and-Desist Order — written order to halt all activity in the affected zone immediately',
        'Remediation Order (Replant / Restore) — compulsory restoration order with compliance deadline',
        'Dispatch On-Site Patrol — physical inspection and deterrence patrol',
        'Refer to Legal / Prosecutor — escalation for criminal proceedings (illegal mining, large-scale clearing)',
        'Assess Environmental Fine — financial penalty assessment per jurisdiction thresholds',
      ]},
      { type: 'p', text: 'Each action requires an Authority / Officer Name and Response Deadline (days). Add a Case Reference Number to cross-reference with your regulatory document management system.' },
      { type: 'note', text: 'All dispatched actions are recorded in the immutable audit trail with a SHA-256 sealed evidence record. The enforcement dossier includes the original satellite anomaly, drone canopy analysis, Step 7 decision, and enforcement details.' },
    ],
  },
  {
    id: 'env-carbon',
    section: 'Environmental Geotech',
    title: 'Step 8b — Carbon Credit Verification',
    icon: 'TreePine',
    keywords: ['carbon', 'credit', 'biomass', 'sequestration', 'agb', 'canopy cover', 'co2', 'offset', 'verification', 'parcel', 'at risk'],
    blocks: [
      { type: 'p', text: 'Carbon Credit Verification aggregates canopy analysis data from all completed environmental drone surveys and provides a portfolio-level biomass integrity view for carbon credit scheme auditing.' },
      { type: 'h3', text: 'Key metrics per parcel' },
      { type: 'table',
        headers: ['Metric', 'Description'],
        rows: [
          ['Canopy Cover %',       'Percentage of the parcel with living canopy — displayed as a colour-coded progress bar (green ≥80%, amber ≥60%, orange ≥40%, red <40%)'],
          ['AGB (t/ha)',           'Above-Ground Biomass estimate in tonnes per hectare, derived from canopy height model and multispectral indices'],
          ['CO₂ Sequestration (t)','Estimated carbon dioxide sequestration in tonnes — aggregated across the parcel from AGB × biomass expansion factor'],
          ['At Risk flag',        'Raised automatically when canopy loss exceeds 5% since the last verified survey — invalidates part of the carbon credit claim'],
        ],
      },
      { type: 'h3', text: 'Portfolio summary' },
      { type: 'p', text: 'The top summary bar shows total CO₂ sequestration (tonnes), average canopy cover across all parcels, and the count of parcels currently flagged "At Risk". Use this view for preparing carbon registry submissions or regulator-facing verification reports.' },
    ],
  },
  {
    id: 'env-geohazard',
    section: 'Environmental Geotech',
    title: 'Geohazard Risk',
    icon: 'TreePine',
    keywords: ['geohazard', 'landslide', 'flood', 'slope', 'sar', 'dem', 'coherence', 'subsidence', 'erosion', 'early warning', 'civil engineering'],
    blocks: [
      { type: 'p', text: 'Geohazard Risk monitors slope instability, flood risk, and ground subsidence events in geohazard monitoring zones using Sentinel-1 SAR coherence analysis and Digital Elevation Model (DEM) change detection.' },
      { type: 'h3', text: 'Running a SAR / DEM scan' },
      { type: 'p', text: 'Click "SAR / DEM Scan" to trigger an immediate analysis across all geohazard zone boundaries. The scan compares current SAR coherence against the baseline to detect surface movement or displacement.' },
      { type: 'h3', text: 'Escalation options' },
      { type: 'table',
        headers: ['Action', 'Effect'],
        rows: [
          ['Dispatch Civil Engineering Inspection', 'Site visit order raised; geotechnical firm notified'],
          ['Issue Early-Warning Alert to Communities', 'Downstream community alert triggered; recorded in audit trail'],
          ['Monitor — Increase Satellite Pass Frequency', 'Elevated monitoring flag set; no immediate field action'],
          ['Reject — False Positive', 'Dismissed from queue; permanently recorded as false positive'],
        ],
      },
      { type: 'note', text: 'Geohazard events with critical severity and pending status are highlighted with a red badge in the navigation sidebar. Early-warning alerts should be issued for any critical landslide or flood-risk event in populated corridors.' },
    ],
  },

  // ── Platform Admin ────────────────────────────────────────────────────────────
  {
    id: 'platform-admin',
    section: 'Platform Administration',
    title: 'Platform Admin overview',
    icon: 'Settings',
    keywords: ['platform', 'admin', 'organisations', 'modules', 'feature flags', 'roles', 'users', 'analytics'],
    blocks: [
      { type: 'p', text: 'The Platform Administration section is only visible to users with the Platform Admin role (permission: *.*). It provides controls over the entire multi-tenant platform.' },
      { type: 'table',
        headers: ['Page', 'Purpose'],
        rows: [
          ['Platform Dashboard', 'Cross-tenant KPIs: active orgs, total users, mission volume, anomaly count'],
          ['Organisations', 'Provision new organisations, view and manage all existing ones'],
          ['Users', 'Global user list with search, role assignment, and deactivation'],
          ['Module Marketplace', 'Enable / disable vertical modules per organisation (AgroTech, Oil & Gas, etc.)'],
          ['Roles', 'Define and manage permission sets available to all organisations'],
          ['Analytics', 'Platform-wide usage analytics and trend charts'],
          ['Membership Requests', 'Review and approve pending organisation membership requests across all orgs'],
          ['User Audit', 'Immutable log of all user management actions across all tenants'],
        ],
      },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Search logic — normalised full-text match across all text content
// ─────────────────────────────────────────────────────────────────────────────

function extractText(article: Article): string {
  const parts: string[] = [article.section, article.title, ...article.keywords];
  for (const block of article.blocks) {
    if (block.text) parts.push(block.text);
    if (block.items) parts.push(...block.items);
    if (block.headers) parts.push(...block.headers);
    if (block.rows) parts.push(...block.rows.flat());
  }
  return parts.join(' ').toLowerCase();
}

const SEARCH_CORPUS = ARTICLES.map((a) => ({ article: a, corpus: extractText(a) }));

function search(query: string): Article[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const words = q.split(/\s+/);
  return SEARCH_CORPUS
    .filter(({ corpus }) => words.every((w) => corpus.includes(w)))
    .map(({ article }) => article);
}

// ─────────────────────────────────────────────────────────────────────────────
// Section grouping for the table of contents
// ─────────────────────────────────────────────────────────────────────────────

export const SECTIONS = Array.from(new Set(ARTICLES.map((a) => a.section)));

export const SECTION_ICONS: Record<string, React.FC<{ className?: string }>> = {
  'Authentication': HelpCircle,
  'Command Center': LayoutDashboard,
  'AgroTech — Step 1': Leaf,
  'AgroTech — Step 2': Leaf,
  'AgroTech — Steps 3 & 4': Leaf,
  'AgroTech — Step 5': Leaf,
  'AgroTech — Step 6': Leaf,
  'AgroTech — Step 7': Leaf,
  'AgroTech — Step 8': Leaf,
  'AgroTech — Step 8.6': Leaf,
  'AgroTech — Reports': Leaf,
  'Precision Planning': Target,
  'Oil & Gas': Flame,
  'Infrastructure Intelligence': Building,
  'Linear Assets Intelligence': Zap,
  'Environmental Geotech': Leaf,
  'Mission Control': MapPin,
  'Satellite Monitoring': Satellite,
  'Settings': Settings,
  'Platform Administration': Settings,
};

// ─────────────────────────────────────────────────────────────────────────────
// Render helpers
// ─────────────────────────────────────────────────────────────────────────────

function highlight(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const words = query.trim().split(/\s+/).filter(Boolean);
  const pattern = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    pattern.test(part)
      ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-700/50 text-inherit rounded px-0.5">{part}</mark>
      : part
  );
}

function ArticleContent({ article, query }: { article: Article; query: string }) {
  const hl = useCallback((t: string) => highlight(t, query), [query]);

  return (
    <div className="space-y-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
      {article.blocks.map((block, i) => {
        switch (block.type) {
          case 'h3':
            return (
              <h3 key={i} className="text-base font-semibold text-gray-900 dark:text-white mt-6 mb-1">
                {hl(block.text!)}
              </h3>
            );
          case 'p':
            return <p key={i}>{hl(block.text!)}</p>;
          case 'note':
            return (
              <div key={i} className="flex gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
                <HelpCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-blue-800 dark:text-blue-300">{hl(block.text!)}</p>
              </div>
            );
          case 'ul':
            return (
              <ul key={i} className="list-disc list-outside ml-5 space-y-1">
                {block.items!.map((item, j) => <li key={j}>{hl(item)}</li>)}
              </ul>
            );
          case 'table':
            return (
              <div key={i} className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      {block.headers!.map((h, j) => (
                        <th key={j} className="px-4 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {hl(h)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {block.rows!.map((row, j) => (
                      <tr key={j} className="even:bg-gray-50/50 dark:even:bg-gray-800/30">
                        {row.map((cell, k) => (
                          <td key={k} className="px-4 py-2 align-top text-gray-700 dark:text-gray-300">
                            {hl(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function HelpPage() {
  useCanonical('/help');
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showHelpChat, setShowHelpChat] = useState(false);
  const [helpChatMinimized, setHelpChatMinimized] = useState(false);
  const [searchParams] = useSearchParams();

  // Auto-open chat when navigated to with ?chat=open (e.g. from ChatSlideOut expander)
  useEffect(() => {
    if (searchParams.get('chat') === 'open') {
      setShowHelpChat(true);
    }
  }, [searchParams]);

  const results = useMemo(() => search(query), [query]);
  const activeArticle = useMemo(() => ARTICLES.find((a) => a.id === activeId) ?? null, [activeId]);

  const handleSelect = (id: string) => {
    setActiveId(id);
    setQuery('');
  };

  const handleBack = () => setActiveId(null);

  // ── Article detail view ────────────────────────────────────────────────────
  if (activeArticle) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={handleBack}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Back to help centre"
            >
              <ArrowLeft className="w-5 h-5 text-gray-500" />
            </button>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{activeArticle.section}</p>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{activeArticle.title}</h1>
            </div>
          </div>
          <button
            onClick={() => setShowHelpChat(!showHelpChat)}
            className="p-2.5 rounded-lg bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400 transition-colors flex-shrink-0"
            title="Open AI help assistant"
          >
            <MessageCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Help Chat */}
        {showHelpChat && (
          <div>
            <HelpChat
              onClose={() => setShowHelpChat(false)}
              isMinimized={helpChatMinimized}
              onMinimizeChange={setHelpChatMinimized}
            />
          </div>
        )}

        <Card>
          <ArticleContent article={activeArticle} query="" />
        </Card>

        {/* Related articles in same section */}
        {(() => {
          const related = ARTICLES.filter((a) => a.section === activeArticle.section && a.id !== activeArticle.id);
          if (related.length === 0) return null;
          return (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                More in {activeArticle.section}
              </p>
              <div className="space-y-1">
                {related.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setActiveId(a.id)}
                    className="w-full text-left flex items-center justify-between px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="text-sm text-gray-700 dark:text-gray-300">{a.title}</span>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // ── Main help centre view ──────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-primary-600" /> Help Centre
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Search the Nabhos user guide or browse by section
          </p>
        </div>
        <button
          onClick={() => setShowHelpChat(!showHelpChat)}
          className="p-2.5 rounded-lg bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 text-primary-600 dark:text-primary-400 transition-colors"
          title="Open AI help assistant"
        >
          <MessageCircle className="w-5 h-5" />
        </button>
      </div>

      {/* Help Chat */}
      {showHelpChat && (
        <div>
          <HelpChat
            onClose={() => setShowHelpChat(false)}
            isMinimized={helpChatMinimized}
            onMinimizeChange={setHelpChatMinimized}
          />
        </div>
      )}

      {/* Search bar */}
      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder={'Search help articles\u2026 (e.g. \u201cNDVI\u201d, \u201cupload\u201d, \u201canomaly\u201d)'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-9 pr-9 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          autoFocus
        />
        {query && (
          <button
            onClick={() => setQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search results */}
      {query.trim() && (
        <div>
          {results.length === 0 ? (
            <Card>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                No articles found for <strong>"{query}"</strong>. Try different keywords.
              </p>
            </Card>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {results.length} article{results.length !== 1 ? 's' : ''} found
              </p>
              {results.map((article) => (
                <SearchResult
                  key={article.id}
                  article={article}
                  query={query}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table of contents — hidden while searching */}
      {!query.trim() && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {SECTIONS.map((section) => {
            const Icon = SECTION_ICONS[section] ?? HelpCircle;
            const articles = ARTICLES.filter((a) => a.section === section);
            return (
              <Card key={section} padding="none">
                {/* Section header */}
                <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
                  <Icon className="w-4 h-4 text-primary-600 flex-shrink-0" />
                  <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{section}</h2>
                  <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{articles.length} article{articles.length !== 1 ? 's' : ''}</span>
                </div>
                {/* Article list */}
                <ul className="divide-y divide-gray-50 dark:divide-gray-800/50">
                  {articles.map((article) => (
                    <li key={article.id}>
                      <button
                        onClick={() => handleSelect(article.id)}
                        className={cn(
                          'w-full text-left flex items-center justify-between px-5 py-3',
                          'hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
                          'text-sm text-gray-700 dark:text-gray-300'
                        )}
                      >
                        <span>{article.title}</span>
                        <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Search result card ─────────────────────────────────────────────────────────

function SearchResult({
  article,
  query,
  onSelect,
}: {
  article: Article;
  query: string;
  onSelect: (id: string) => void;
}) {
  // Find the first block that contains a match to use as a snippet
  const q = query.trim().toLowerCase();
  const snippet = useMemo(() => {
    for (const block of article.blocks) {
      const candidates: string[] = [];
      if (block.text) candidates.push(block.text);
      if (block.items) candidates.push(...block.items);
      if (block.rows) candidates.push(...block.rows.flat());
      for (const c of candidates) {
        if (c.toLowerCase().includes(q)) {
          return c.length > 120 ? c.slice(0, 120) + '…' : c;
        }
      }
    }
    return null;
  }, [article, q]);

  return (
    <button
      onClick={() => onSelect(article.id)}
      className="w-full text-left"
    >
      <Card hover padding="sm" className="transition-colors">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-primary-600 dark:text-primary-400 font-medium mb-0.5">{article.section}</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{highlight(article.title, query)}</p>
            {snippet && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                {highlight(snippet, query)}
              </p>
            )}
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
        </div>
      </Card>
    </button>
  );
}

// Made with Bob
